import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Settings as SettingsIcon, Shield, Wrench, Package, Plug, Bell, Brain,
  Cpu, RefreshCw, FileText, Grid3x3, GitBranch, Trash2, Activity, Key,
  Server, HardDrive, MemoryStick, Wifi, Zap, Thermometer, Clock,
  Sparkles, Bot, Lightbulb, Rocket, Code, Copy, Play, TrendingUp,
  AlertCircle, AlertTriangle, CheckCircle, XCircle, Database, Network, Eye,
  Download, Upload, Gauge, Timer, BarChart3, PieChart, Globe, Lock,
  MapPin, Signal, ShieldCheck, ShieldOff, Power, Settings2, FileKey,
  Plus, Pencil, Trash, ToggleLeft, ToggleRight, PenTool, X, FolderUp,
  History, Users, CreditCard, Wallet, Crown, Info
} from 'lucide-react';
import { useI18n } from '../../i18n';
import useAuthStore from '../../store/authStore';
import { vpn, servers } from '../../api/client';
import { toast } from '../../utils/toastUtils';
import SplunkWizard from '../../components/wizards/SplunkWizard';
import JiraWizard from '../../components/wizards/JiraWizard';
import GenericWebhookWizard from '../../components/wizards/GenericWebhookWizard';
import EmailWizard from '../../components/wizards/EmailWizard';
import AIWorkflowVisual from '../../components/AIWorkflowVisual';
import SystemInfo from '../../components/SystemInfo';
import ToolsList from '../../components/ToolsList';
import GPUInfo from '../../components/GPUInfo';
import AttackMatrixConfig from '../../components/AttackMatrixConfig';
import LLMLogicConfig from '../../components/LLMLogicConfig';
import SecuritySettings from '../../components/SecuritySettings';
import ResetDataSettings from '../../components/ResetDataSettings';
import ModulesConfiguration from '../../components/ModulesConfiguration';
import IntegrationManualConfigModal from '../../components/IntegrationManualConfigModal';
import UpdatesManager from '../../components/UpdatesManager';
import NotificationsConfig from '../../components/NotificationsConfig';
import LogsViewer from '../../components/LogsViewer';
import LicenseSettings from '../../components/LicenseSettings';
import OrchestratorMonitoringEmbed from '../Orchestrator/OrchestratorMonitoring';
import AIDecisionsConfig from '../../components/AIDecisionsConfig';
import ReportsConfig from '../../components/ReportsConfig';
import ContractManager from '../../components/ContractManager';
import ConnectorsConfig from '../../components/ConnectorsConfig';
import ResourceCharts from '../../components/ResourceCharts';
import DynamicOrchestratorTools from '../../components/DynamicOrchestratorTools';
import TrackingLogs from '../../components/TrackingLogs';
import AuditLogs from '../../components/AuditLogs';
import ClientsManagement from '../../components/ClientsManagement';
import CreditsSettings from '../../components/CreditsSettings';
import { CreditsAdminPanel } from '../../components/admin/credits';
import { AIAgentDashboard } from './AIAgent';
import AIAssistantSettings from './AIAssistantSettings';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ─────────────────────────────────────────────
// Server resources cache (module-level — not re-created on each render)
// ─────────────────────────────────────────────
const RESOURCES_CACHE_KEY = 'settings_server_resources_cache';
const RESOURCES_CACHE_TTL = 30 * 1000; // 30 secondes

const getResourcesCache = () => {
  try {
    const raw = localStorage.getItem(RESOURCES_CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > RESOURCES_CACHE_TTL) {
      localStorage.removeItem(RESOURCES_CACHE_KEY);
      return null;
    }
    return data;
  } catch { return null; }
};

const setResourcesCache = (data) => {
  try {
    localStorage.setItem(RESOURCES_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {}
};

// ─────────────────────────────────────────────
// Proxy Settings Component
// ─────────────────────────────────────────────
const ProxySettings = () => {
  const [proxyStats, setProxyStats] = useState(null);
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testUrl, setTestUrl] = useState('https://ifconfig.me');
  const [selectedProtocol, setSelectedProtocol] = useState('all');
  const [customProxy, setCustomProxy] = useState({ url: '', protocol: 'http', username: '', password: '' });
  const [customProxies, setCustomProxies] = useState([]);
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const fetchProxyStats = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/v1/proxy/stats`, { headers: getAuthHeaders() });
      if (r.ok) setProxyStats(await r.json());
    } catch {}
  };

  const fetchProxies = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 20 });
      if (selectedProtocol !== 'all') params.append('protocol', selectedProtocol);
      const r = await fetch(`${API_BASE}/api/v1/proxy/list?${params}`, { headers: getAuthHeaders() });
      if (r.ok) setProxies((await r.json()).proxies || []);
    } catch {} finally { setLoading(false); }
  };

  const refreshPool = async () => {
    setRefreshing(true);
    try {
      await fetch(`${API_BASE}/api/v1/proxy/refresh`, { method: 'POST', headers: getAuthHeaders() });
      await fetchProxies();
      await fetchProxyStats();
    } catch {} finally { setRefreshing(false); }
  };

  const testProxy = async (proxy) => {
    setTestResult({ loading: true, proxy });
    try {
      const r = await fetch(`${API_BASE}/api/v1/proxy/test`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ proxy, test_url: testUrl }),
      });
      const data = await r.json();
      setTestResult({ ...data, proxy });
    } catch (e) { setTestResult({ success: false, error: String(e), proxy }); }
  };

  useEffect(() => { fetchProxyStats(); fetchProxies(); }, [selectedProtocol]);

  const addCustomProxy = () => {
    if (!customProxy.url) return;
    setCustomProxies(prev => [...prev, { ...customProxy, id: Date.now(), source: 'manual' }]);
    setCustomProxy({ url: '', protocol: 'http', username: '', password: '' });
  };

  const protocolColors = { http: 'text-blue-400', socks4: 'text-yellow-400', socks5: 'text-green-400' };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-brand-green mb-1">Proxy</h2>
          <p className="text-gray-400 text-sm">Pool de proxies HTTP/SOCKS pour anonymiser le trafic de scan</p>
        </div>
        <button onClick={refreshPool} disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/40 rounded-lg hover:bg-brand-green/30 transition-colors text-sm">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser le pool
        </button>
      </div>

      {/* Stats */}
      {proxyStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total disponibles', value: proxyStats.total || 0, color: 'blue', icon: <Network className="w-5 h-5" /> },
            { label: 'HTTP', value: proxyStats.by_protocol?.http || 0, color: 'blue', icon: <Globe className="w-5 h-5" /> },
            { label: 'SOCKS4', value: proxyStats.by_protocol?.socks4 || 0, color: 'yellow', icon: <Lock className="w-5 h-5" /> },
            { label: 'SOCKS5', value: proxyStats.by_protocol?.socks5 || 0, color: 'green', icon: <Shield className="w-5 h-5" /> },
          ].map(s => (
            <div key={s.label} className={`bg-brand-black-light border border-${s.color}-500/30 rounded-xl p-4`}>
              <div className={`text-${s.color}-400 mb-2`}>{s.icon}</div>
              <div className="text-2xl font-bold text-white">{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Protocol filter + proxy list */}
      <div className="bg-brand-black-light border border-gray-700 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-300">Pool de proxies automatiques</h3>
          <div className="flex gap-2">
            {['all', 'http', 'socks4', 'socks5'].map(p => (
              <button key={p} onClick={() => setSelectedProtocol(p)}
                className={`px-3 py-1 text-xs rounded-lg border transition-colors ${selectedProtocol === p ? 'bg-brand-green text-black border-brand-green' : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}>
                {p === 'all' ? 'Tous' : p.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-4"><RefreshCw className="w-4 h-4 animate-spin" /> Chargement...</div>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {proxies.length === 0 ? (
              <div className="text-gray-500 text-sm py-4 text-center">Aucun proxy disponible — cliquez sur "Actualiser le pool"</div>
            ) : proxies.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-brand-black transition-colors">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-mono font-bold ${protocolColors[p.protocol] || 'text-gray-400'}`}>{(p.protocol || 'http').toUpperCase()}</span>
                  <span className="text-sm font-mono text-white">{p.ip}:{p.port}</span>
                  {p.country && <span className="text-xs text-gray-500">{p.country}</span>}
                  {p.anonymity && <span className={`text-xs px-2 py-0.5 rounded-full ${p.anonymity === 'elite' ? 'bg-green-500/20 text-green-400' : p.anonymity === 'anonymous' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>{p.anonymity}</span>}
                </div>
                <button onClick={() => testProxy(`${p.protocol}://${p.ip}:${p.port}`)}
                  className="text-xs px-2 py-1 bg-brand-green/10 text-brand-green rounded hover:bg-brand-green/20 transition-colors">
                  Tester
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Test result */}
      {testResult && !testResult.loading && (
        <div className={`border rounded-xl p-4 ${testResult.success ? 'border-green-500/40 bg-green-500/10' : 'border-red-500/40 bg-red-500/10'}`}>
          <div className="flex items-center gap-2">
            {testResult.success ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
            <span className={`text-sm font-semibold ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
              {testResult.success ? `Proxy fonctionnel — ${testResult.response_time_ms || 0}ms` : `Proxy non fonctionnel — ${testResult.error || 'Erreur inconnue'}`}
            </span>
            <span className="text-xs text-gray-500 ml-2">{testResult.proxy}</span>
          </div>
        </div>
      )}

      {/* Custom Proxy config */}
      <div className="bg-brand-black-light border border-gray-700 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Proxies manuels</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div className="md:col-span-2">
            <input type="text" placeholder="host:port ou http://host:port" value={customProxy.url}
              onChange={e => setCustomProxy(p => ({ ...p, url: e.target.value }))}
              className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-green" />
          </div>
          <select value={customProxy.protocol} onChange={e => setCustomProxy(p => ({ ...p, protocol: e.target.value }))}
            className="px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-brand-green">
            <option value="http">HTTP</option>
            <option value="socks4">SOCKS4</option>
            <option value="socks5">SOCKS5</option>
          </select>
          <button onClick={addCustomProxy} className="flex items-center justify-center gap-2 px-4 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/80 transition-colors text-sm font-semibold">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
        {customProxies.length > 0 && (
          <div className="space-y-1 mt-3">
            {customProxies.map(p => (
              <div key={p.id} className="flex items-center justify-between py-2 px-3 bg-brand-black rounded-lg">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-mono font-bold ${protocolColors[p.protocol] || 'text-gray-400'}`}>{p.protocol.toUpperCase()}</span>
                  <span className="text-sm font-mono text-white">{p.url}</span>
                  <span className="text-xs px-2 py-0.5 bg-gray-700 text-gray-400 rounded">Manuel</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => testProxy(`${p.protocol}://${p.url}`)}
                    className="text-xs px-2 py-1 bg-brand-green/10 text-brand-green rounded hover:bg-brand-green/20">Tester</button>
                  <button onClick={() => setCustomProxies(prev => prev.filter(x => x.id !== p.id))}
                    className="text-xs p-1 text-red-400 hover:text-red-300"><Trash className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-gray-400">
            <p className="font-semibold text-blue-400 mb-1">Comment utiliser les proxies</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>In the AI Scan Wizard → Step 3 → Network Routing → select <strong>Proxy</strong></li>
              <li>Saisir l'URL au format <code className="text-blue-300">socks5://host:port</code> ou <code className="text-blue-300">http://host:port</code></li>
              <li>Automatic proxies are refreshed from ProxyScrape (pool public)</li>
              <li>Toujours tester un proxy avant de l'utiliser en production</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// AI API Keys Configuration Component
// ─────────────────────────────────────────────
const AIApiKeysConfig = () => {
  const [keys, setKeys] = useState({
    claude: '', openai: '', grok: '', gemini: '', codex: '', kimi: ''
  });
  const [showKeys, setShowKeys] = useState({ claude: false, openai: false, grok: false, gemini: false, codex: false, kimi: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  useEffect(() => {
    // Load saved keys from backend
    fetch(`${API_BASE}/api/v1/settings/ai-api-keys`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setKeys({ claude: d.claude || '', openai: d.openai || '', grok: d.grok || '', gemini: d.gemini || '', codex: d.codex || '', kimi: d.kimi || '' }); })
      .catch(() => {});
  }, []);

  const saveKey = async (provider) => {
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/v1/settings/ai-api-keys`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ provider, api_key: keys[provider] }),
      });
      if (r.ok) {
        setSaved(p => ({ ...p, [provider]: true }));
        setTimeout(() => setSaved(p => ({ ...p, [provider]: false })), 3000);
      }
    } catch {} finally { setSaving(false); }
  };

  const providers = [
    {
      id: 'claude', name: 'Claude (Anthropic)', logo: '🤖',
      description: 'claude-opus-4-6, claude-sonnet-4-6, claude-haiku-4-5',
      placeholder: 'sk-ant-api03-...',
      docsUrl: 'https://console.anthropic.com/account/keys',
      color: 'orange',
    },
    {
      id: 'openai', name: 'OpenAI', logo: '🧠',
      description: 'GPT-4o, GPT-4-turbo, o1, o3-mini',
      placeholder: 'sk-proj-...',
      docsUrl: 'https://platform.openai.com/api-keys',
      color: 'green',
    },
    {
      id: 'codex', name: 'Codex (OpenAI)', logo: '💻',
      description: 'codex-mini, codex-full — analyse code & vulns',
      placeholder: 'sk-proj-...',
      docsUrl: 'https://platform.openai.com/api-keys',
      color: 'cyan',
    },
    {
      id: 'grok', name: 'Grok (xAI)', logo: '⚡',
      description: 'grok-3, grok-3-mini, grok-2-vision',
      placeholder: 'xai-...',
      docsUrl: 'https://console.x.ai/keys',
      color: 'blue',
    },
    {
      id: 'gemini', name: 'Gemini (Google)', logo: '✨',
      description: 'gemini-2.0-flash, gemini-1.5-pro, gemini-1.5-flash',
      placeholder: 'AIza...',
      docsUrl: 'https://aistudio.google.com/app/apikey',
      color: 'purple',
    },
    {
      id: 'kimi', name: 'Kimi (Moonshot AI)', logo: '🌙',
      description: 'moonshot-v1-8k, moonshot-v1-32k, kimi-latest',
      placeholder: 'sk-...',
      docsUrl: 'https://platform.moonshot.cn/console/api-keys',
      color: 'indigo',
    },
  ];

  return (
    <div className="bg-brand-black-light border border-brand-green/20 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 bg-brand-green/10 rounded-lg">
          <Key className="w-5 h-5 text-brand-green" />
        </div>
        <div>
          <h3 className="text-base font-bold text-white">API Keys — External LLMs</h3>
          <p className="text-xs text-gray-500">Connect external AI models for analysis and reports</p>
        </div>
        <div className="ml-auto flex items-center gap-1 text-xs px-2 py-1 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-lg">
          <ShieldCheck className="w-3 h-3" />
          Encrypted in database
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {providers.map(p => (
          <div key={p.id} className={`bg-brand-black border border-${p.color}-500/20 rounded-xl p-4 hover:border-${p.color}-500/40 transition-colors`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">{p.logo}</span>
              <div>
                <div className="text-sm font-semibold text-white">{p.name}</div>
                <div className="text-xs text-gray-500">{p.description}</div>
              </div>
              {saved[p.id] && (
                <div className="ml-auto flex items-center gap-1 text-xs text-green-400">
                  <CheckCircle className="w-3 h-3" /> Saved
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showKeys[p.id] ? 'text' : 'password'}
                  value={keys[p.id]}
                  onChange={e => setKeys(prev => ({ ...prev, [p.id]: e.target.value }))}
                  placeholder={p.placeholder}
                  className="w-full px-3 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-green pr-10"
                />
                <button onClick={() => setShowKeys(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                  {showKeys[p.id] ? <Eye className="w-4 h-4" /> : <Eye className="w-4 h-4 opacity-50" />}
                </button>
              </div>
              <button onClick={() => saveKey(p.id)} disabled={saving || !keys[p.id]}
                className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40 ${saved[p.id] ? 'bg-green-500/20 text-green-400 border border-green-500/40' : 'bg-brand-green text-black hover:bg-brand-green/80'}`}>
                {saved[p.id] ? <CheckCircle className="w-4 h-4" /> : 'Sauvegarder'}
              </button>
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Get a key: <a href={p.docsUrl} target="_blank" rel="noopener noreferrer"
                className={`text-${p.color}-400 hover:underline`}>{p.docsUrl.replace('https://', '')}</a>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 bg-brand-black rounded-lg border border-gray-800">
        <p className="text-xs text-gray-500">
          <span className="text-gray-400 font-medium">Default active model:</span> Ollama local (sylink/sylink2:27b) —
          external API keys allow using cloud models as fallback or for specific reports.
          Keys are stored encrypted in the database and never exposed in plaintext.
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Settings Component
// ─────────────────────────────────────────────
const Settings = () => {
  const { t } = useI18n();
  const { user: storeUser, fetchUser } = useAuthStore();
  // Initialize with store user to avoid loading state if user data is already available
  // SuperAdminRoute has already verified the user, so we can trust the store data
  const initialUser = useAuthStore.getState().user;
  const [user, setUser] = useState(initialUser);
  // Only show loading if we don't have initial user data
  const [isLoadingUser, setIsLoadingUser] = useState(!initialUser);

  // Refresh user data in background to ensure we have fresh is_superuser status
  // Don't block UI if we already have user data from store
  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const loadUserData = async () => {
      // Only set loading to true if we don't have initial user data
      if (!initialUser) {
        setIsLoadingUser(true);
      }
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');

        if (token) {
          const response = await fetch(`${API_BASE}/api/v1/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          });

          // Handle 401 - token expired, redirect to login
          if (response.status === 401) {
            localStorage.removeItem('access_token');
            window.location.href = '/login';
            return;
          }

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            // API failed - use store data if available
            const currentStoreUser = useAuthStore.getState().user;
            if (currentStoreUser) {
              setUser(currentStoreUser);
            } else {
              await fetchUser();
              setUser(useAuthStore.getState().user || null);
            }
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        if (error?.name === 'AbortError') {
          console.warn('[Settings] User data fetch timed out, using store data');
        } else {
          console.warn('[Settings] Error fetching user:', error?.message || error);
        }
        // Use store data as fallback
        const fallbackUser = useAuthStore.getState().user;
        if (fallbackUser && !user) {
          setUser(fallbackUser);
        }
      } finally {
        clearTimeout(timeoutId);
        setIsLoadingUser(false);
      }
    };
    loadUserData();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []); // Run only on mount - storeUser is accessed via getState() to avoid stale closure

  const isSuperAdmin = user?.is_super_admin || user?.is_superuser || user?.role === 'super_admin';

  const [activeTab, setActiveTab] = useState('general');
  const [activeWizard, setActiveWizard] = useState(null);
  const [manualConfigModal, setManualConfigModal] = useState(null);

  // Demo invitation state
  const [demoInviteForm, setDemoInviteForm] = useState({ email: '', firstName: '', lastName: '', companyName: '', message: '' });
  const [demoInviteLoading, setDemoInviteLoading] = useState(false);
  const [demoInviteSuccess, setDemoInviteSuccess] = useState(null);
  const [integrations, setIntegrations] = useState({
    splunk: { configured: false },
    jira: { configured: false },
    elasticsearch: { configured: false },
    slack: { configured: false },
    teams: { configured: false },
    discord: { configured: false },
    mattermost: { configured: false },
    s3: { configured: false },
    prometheus: { configured: false },
    sekoia: { configured: false },
    kafka: { configured: false },
    wazuh: { configured: false },
    email: { configured: false },
  });

  // Server resources state — lazy initializer : lit le cache une seule fois au mount
  const [serverResources, setServerResources] = useState(() =>
    getResourcesCache() || {
      cpu: { usage: 0, cores: 0, model: 'Chargement...' },
      memory: { used: 0, total: 0, percent: 0 },
      disk: { used: 0, total: 0, percent: 0 },
      gpu: { name: 'Chargement...', memory_used: 0, memory_total: 0, utilization: 0, temperature: 0, available: false },
      network: { rx_bytes: 0, tx_bytes: 0, rx_rate: 0, tx_rate: 0 },
      uptime: 0,
      load_average: [0, 0, 0],
      processes: 0,
      docker_containers: 0
    }
  );
  // loading=false if we have a valid cache (no skeleton on load)
  const [resourcesLoading, setResourcesLoading] = useState(() => !getResourcesCache());
  // Separate state for the manual Refresh button (doesn't block the button)
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  // AI Assistant state
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Auto-optimization state
  const [optimizationSuggestions, setOptimizationSuggestions] = useState([]);
  const [applyingOptimization, setApplyingOptimization] = useState(null);

  // VPN state
  const [vpnConfigs, setVpnConfigs] = useState({ externe: [], interne: [] });
  const [vpnStatus, setVpnStatus] = useState({ status: 'disconnected', config_id: null });
  const [vpnLoading, setVpnLoading] = useState(true);
  const [vpnAddModal, setVpnAddModal] = useState(false);
  const [vpnEditModal, setVpnEditModal] = useState(null);
  const [connectingVpn, setConnectingVpn] = useState(null);
  const [vpnUploadFile, setVpnUploadFile] = useState(null);
  const [vpnUploadType, setVpnUploadType] = useState('externe');
  const [vpnCredentials, setVpnCredentials] = useState({ username: '', password: '' });
  const [vpnFilter, setVpnFilter] = useState('');
  // VPN Credentials Modal
  const [vpnCredentialsModal, setVpnCredentialsModal] = useState(null);
  const [vpnConnectionError, setVpnConnectionError] = useState(null);
  const [vpnConnectingInModal, setVpnConnectingInModal] = useState(false);
  const [vpnSaveCredentials, setVpnSaveCredentials] = useState(false);
  const [savedVpnCredentials, setSavedVpnCredentials] = useState({});

  // Servers/VPS Monitoring state
  const [serversList, setServersList] = useState([]);
  const [serversStats, setServersStats] = useState(null);
  const [serversLoading, setServersLoading] = useState(true);
  const [serversRefreshing, setServersRefreshing] = useState(false);
  const [serverInfoModal, setServerInfoModal] = useState(null);

  // Server type descriptions and tools
  const serverTypeInfo = {
    backend: {
      title: "Serveur Backend",
      description: "Main server running the SYLink application. Handles API requests, scan orchestration and business logic.",
      tools: ["Python/FastAPI", "Client PostgreSQL", "Client Redis", "Celery Worker", "Docker", "Nmap", "Nuclei", "SQLMap", "Nikto"]
    },
    scanner: {
      title: "VPS Scanner/VPN",
      description: "Dedicated VPS server for security scans. Used to anonymize scans via VPN and distribute load.",
      tools: ["Surfshark VPN", "Nmap", "Masscan", "Nuclei", "Nikto", "WPScan", "SQLMap", "Hydra", "Metasploit"]
    },
    database: {
      title: "Database Server",
      description: "Server hosting PostgreSQL and Redis. Stores all application data.",
      tools: ["PostgreSQL 16", "Redis", "pg_dump", "Sauvegarde automatique"]
    },
    haproxy: {
      title: "HAProxy Load Balancer",
      description: "Load balancer and reverse proxy. Distributes traffic between backend servers.",
      tools: ["HAProxy", "SSL/TLS Termination", "Health Check", "Rate Limiting"]
    },
    gpu: {
      title: "Serveur GPU",
      description: "Server with dedicated GPU for AI models and password cracking.",
      tools: ["NVIDIA CUDA", "Ollama", "Hashcat", "John the Ripper"]
    },
    ollama: {
      title: "Serveur Ollama",
      description: "Server hosting language models for AI vulnerability analysis.",
      tools: ["Ollama", "SYLink Thinking 32B", "Qwen3 8B/14B", "DeepSeek R1"]
    },
    openvas: {
      title: "Serveur OpenVAS",
      description: "OpenVAS/Greenbone vulnerability scanner for comprehensive network audits.",
      tools: ["OpenVAS", "Greenbone Vulnerability Manager", "Flux NVT"]
    },
    metasploit: {
      title: "Serveur Metasploit",
      description: "Exploitation framework for advanced penetration testing.",
      tools: ["Metasploit", "Meterpreter", "Base d'exploits", "Modules post-exploitation"]
    },
    vpn: {
      title: "Serveur VPN",
      description: "VPN server for secure connections and anonymization.",
      tools: ["WireGuard", "OpenVPN", "IPSec", "Surfshark"]
    },
    other: {
      title: "Serveur",
      description: "General infrastructure server.",
      tools: ["SSH", "Surveillance"]
    }
  };
  const [addServerModal, setAddServerModal] = useState(false);
  const [newServer, setNewServer] = useState({
    name: '',
    ip_address: '',
    hostname: '',
    port: 22,
    server_type: 'backend',
    description: '',
    location: '',
    ssh_user: 'root',
    ssh_password: '',
    tags: []
  });

  // Fetch servers list (silent refresh if showLoading is false)
  const fetchServers = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setServersLoading(true);
      }
      const [serversData, statsData] = await Promise.all([
        servers.list(true),
        servers.getStats()
      ]);
      setServersList(serversData);
      setServersStats(statsData);
    } catch (err) {
      // Silent handling for network errors during background refresh
      const isNetworkError = !err.response && (
        err.message?.includes('Network Error') ||
        err.message?.includes('ERR_NETWORK') ||
        err.message?.includes('Failed to fetch') ||
        err.name === 'TypeError'
      );
      if (!isNetworkError && process.env.NODE_ENV === 'development') {
        console.warn('Servers fetch error:', err);
      }
      // Only show toast error if this was an explicit loading request (not background refresh)
      // and it's not a network error (which will auto-recover)
      if (showLoading && !isNetworkError) {
        toast.error('Erreur lors du chargement des serveurs');
      }
    } finally {
      setServersLoading(false);
    }
  }, []);

  // Refresh all servers metrics
  const refreshAllServers = async () => {
    try {
      setServersRefreshing(true);
      await servers.refreshAll();
      await fetchServers();
      toast.success('Metrics refreshed');
    } catch (err) {
      // Silently handle network errors
      const isNetworkError = !err.response && (
        err.message?.includes('Network Error') ||
        err.message?.includes('ERR_NETWORK') ||
        err.message?.includes('Failed to fetch') ||
        err.name === 'TypeError'
      );
      if (!isNetworkError) {
        console.error('Failed to refresh servers:', err);
        toast.error('Error refreshing');
      }
    } finally {
      setServersRefreshing(false);
    }
  };

  // Add a new server
  const addServer = async () => {
    try {
      await servers.create(newServer);
      toast.success('Server added');
      setAddServerModal(false);
      setNewServer({
        name: '',
        ip_address: '',
        hostname: '',
        port: 22,
        server_type: 'backend',
        description: '',
        location: '',
        ssh_user: 'root',
        ssh_password: '',
        tags: []
      });
      await fetchServers();
    } catch (err) {
      const isNetworkError = err.message?.includes('ERR_NETWORK') || err.message?.includes('Failed to fetch');
      if (!isNetworkError) console.error('Failed to add server:', err);
      toast.error(isNetworkError ? 'Network error, please try again' : 'Erreur lors de l\'ajout du serveur');
    }
  };

  // Delete a server
  const deleteServer = async (serverId) => {
    if (!window.confirm('Supprimer ce serveur ?')) return;
    try {
      await servers.delete(serverId);
      toast.success('Server deleted');
      await fetchServers();
    } catch (err) {
      const isNetworkError = err.message?.includes('ERR_NETWORK') || err.message?.includes('Failed to fetch');
      if (!isNetworkError) console.error('Failed to delete server:', err);
      toast.error(isNetworkError ? 'Network error, please try again' : 'Deletion error');
    }
  };

  // Refresh single server
  const refreshServer = async (serverId) => {
    try {
      await servers.refresh(serverId);
      await fetchServers();
      toast.success('Server refreshed');
    } catch (err) {
      const isNetworkError = err.message?.includes('ERR_NETWORK') || err.message?.includes('Failed to fetch');
      if (!isNetworkError) console.error('Failed to refresh server:', err);
      toast.error(isNetworkError ? 'Network error, please try again' : 'Refresh error');
    }
  };

  // Fetch VPN configs from API
  const fetchVpnConfigs = useCallback(async () => {
    try {
      setVpnLoading(true);
      const data = await vpn.getConfigs();
      setVpnConfigs(data);
    } catch (err) {
      // Silent handling for network errors
      if (process.env.NODE_ENV === 'development' && !(err.name === 'TypeError' && err.message === 'Failed to fetch')) {
        console.warn('VPN configs fetch error:', err);
      }
    } finally {
      setVpnLoading(false);
    }
  }, []);

  // Fetch VPN status
  const fetchVpnStatus = useCallback(async () => {
    try {
      const data = await vpn.getStatus();
      setVpnStatus(data);
    } catch (err) {
      // Silently handle 401/403 errors - auth interceptor handles redirect
      if (err.response?.status === 401 || err.response?.status === 403 || err.isAuthRedirect) {
        return;
      }
      // Silent handling for network errors
      if (process.env.NODE_ENV === 'development' && !(err.name === 'TypeError' && err.message === 'Failed to fetch')) {
        console.warn('VPN status fetch error:', err);
      }
    }
  }, []);

  // Load saved VPN credentials from localStorage
  const loadSavedVpnCredentials = useCallback(() => {
    try {
      const saved = localStorage.getItem('vpn_credentials');
      if (saved) {
        setSavedVpnCredentials(JSON.parse(saved));
      }
    } catch (err) {
      console.error('Failed to load VPN credentials:', err);
    }
  }, []);

  // Save VPN credentials to localStorage (encrypted would be better via backend)
  const saveVpnCredentialsLocal = (configId, username, password) => {
    try {
      const newCredentials = { ...savedVpnCredentials, [configId]: { username, password } };
      setSavedVpnCredentials(newCredentials);
      localStorage.setItem('vpn_credentials', JSON.stringify(newCredentials));
    } catch (err) {
      console.error('Failed to save VPN credentials:', err);
    }
  };

  // Open VPN credentials modal
  const openVpnCredentialsModal = (config) => {
    // Check if we have saved credentials for this config
    const saved = savedVpnCredentials[config.id];
    setVpnCredentials({
      username: saved?.username || '',
      password: saved?.password || ''
    });
    setVpnSaveCredentials(!!saved);
    setVpnConnectionError(null);
    setVpnConnectingInModal(false);
    setVpnCredentialsModal(config);
  };

  // Poll VPN status until connected or error
  const pollVpnStatusUntilComplete = async (configName, toastId, maxAttempts = 15) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds between polls

      try {
        const status = await vpn.getStatus();

        if (status.status === 'connected') {
          toast.success(`VPN "${configName}" connected successfully!`, { id: toastId });
          setVpnStatus(status);
          setConnectingVpn(null);
          setVpnCredentialsModal(null);
          setVpnConnectingInModal(false);
          return true;
        } else if (status.status === 'error' || status.status === 'disconnected') {
          // Check if it actually failed or just disconnected
          if (attempt > 2) { // Only consider it failed after a few attempts
            toast.error(`VPN connection failed "${configName}"`, { id: toastId });
            setVpnConnectionError('VPN connection failed. Check credentials or OpenVPN logs.');
            setVpnConnectingInModal(false);
            setConnectingVpn(null);
            return false;
          }
        }
        // Status is still 'connecting' - continue polling
        setVpnStatus(status);
      } catch (err) {
        // Silently handle network errors during polling
        const isNetworkError = err.message?.includes('ERR_NETWORK') || err.message?.includes('Failed to fetch');
        if (!isNetworkError) console.error('Error polling VPN status:', err);
      }
    }

    // Timeout - max attempts reached
    toast.error(`Timeout: La connexion VPN "${configName}" prend trop de temps`, { id: toastId });
    setVpnConnectionError('Timeout: VPN connection expired. Check your network and credentials.');
    setVpnConnectingInModal(false);
    setConnectingVpn(null);
    fetchVpnStatus();
    return false;
  };

  // Connect to VPN after credentials entered
  const handleVpnConnectWithCredentials = async () => {
    if (!vpnCredentialsModal) return;

    const configId = vpnCredentialsModal.id;
    const configName = vpnCredentialsModal.name || configId;
    setVpnConnectionError(null);
    setVpnConnectingInModal(true);
    setConnectingVpn(configId);

    // Show loading toast
    const toastId = toast.loading(`Connexion VPN "${configName}" en cours...`);

    try {
      // Save credentials if checkbox is checked
      if (vpnSaveCredentials && vpnCredentials.username) {
        saveVpnCredentialsLocal(configId, vpnCredentials.username, vpnCredentials.password);
      }

      const result = await vpn.connect(
        configId,
        vpnCredentials.username || null,
        vpnCredentials.password || null
      );

      if (result.status === 'connecting') {
        // Connection initiated - now poll until connected or error
        toast.loading(`Etablissement de la connexion VPN "${configName}"...`, { id: toastId });
        pollVpnStatusUntilComplete(configName, toastId);
      } else if (result.status === 'connected') {
        // Already connected (unlikely but handle it)
        toast.success(`VPN "${configName}" connected successfully!`, { id: toastId });
        setVpnCredentialsModal(null);
        setConnectingVpn(null);
        setVpnConnectingInModal(false);
        fetchVpnStatus();
      } else if (result.status === 'error' || result.status === 'failed') {
        // Connection failed - show error in modal
        const errorMsg = result.message || 'VPN connection failed';
        toast.error(`Echec VPN: ${errorMsg}`, { id: toastId });
        setVpnConnectionError(errorMsg);
        setVpnConnectingInModal(false);
        setConnectingVpn(null);
      }
    } catch (err) {
      const isNetworkError = err.message?.includes('ERR_NETWORK') || err.message?.includes('Failed to fetch');
      if (!isNetworkError) console.error('VPN connect failed:', err);
      // Extract error message from API response
      let errorMessage = isNetworkError ? 'Network error, please try again' : 'VPN connection failed';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      toast.error(`Echec VPN: ${errorMessage}`, { id: toastId });
      setVpnConnectionError(errorMessage);
      setVpnConnectingInModal(false);
    }
  };

  // Connect to VPN - now opens modal for credentials
  const handleVpnConnect = async (configId, requiresAuth = false) => {
    // Find the config
    const allConfigs = [...(vpnConfigs.externe || []), ...(vpnConfigs.interne || [])];
    const config = allConfigs.find(c => c.id === configId);

    if (!config) {
      console.error('Config not found:', configId);
      return;
    }

    // For external VPNs (Surfshark etc), always ask for credentials
    if (config.type === 'externe' || requiresAuth) {
      openVpnCredentialsModal(config);
    } else {
      // Internal VPN without auth requirement - connect directly
      try {
        setConnectingVpn(configId);
        const result = await vpn.connect(configId, null, null);
        if (result.status === 'connecting' || result.status === 'connected') {
          setTimeout(() => {
            fetchVpnStatus();
            setConnectingVpn(null);
          }, 3000);
        }
      } catch (err) {
        const isNetworkError = err.message?.includes('ERR_NETWORK') || err.message?.includes('Failed to fetch');
        if (!isNetworkError) console.error('VPN connect failed:', err);
        setConnectingVpn(null);
      }
    }
  };

  // Disconnect from VPN
  const handleVpnDisconnect = async () => {
    const toastId = toast.loading('Deconnexion VPN en cours...');
    try {
      setConnectingVpn('disconnecting');
      await vpn.disconnect();
      toast.success('VPN disconnected successfully!', { id: toastId });
      setTimeout(() => {
        fetchVpnStatus();
        setConnectingVpn(null);
      }, 2000);
    } catch (err) {
      const isNetworkError = err.message?.includes('ERR_NETWORK') || err.message?.includes('Failed to fetch');
      if (!isNetworkError) console.error('VPN disconnect failed:', err);
      toast.error(isNetworkError ? 'Network error, please try again' : 'VPN disconnection failed', { id: toastId });
      setConnectingVpn(null);
    }
  };

  // Upload VPN config
  const handleVpnUpload = async () => {
    if (!vpnUploadFile) return;
    try {
      await vpn.upload(vpnUploadFile, vpnUploadType);
      setVpnUploadFile(null);
      setVpnAddModal(false);
      fetchVpnConfigs();
    } catch (err) {
      const isNetworkError = err.message?.includes('ERR_NETWORK') || err.message?.includes('Failed to fetch');
      if (!isNetworkError) console.error('VPN upload failed:', err);
      toast.error(isNetworkError ? 'Network error, please try again' : 'Echec de l\'upload VPN');
    }
  };

  // Delete VPN config
  const handleVpnDelete = async (configId) => {
    if (!window.confirm('Supprimer cette configuration VPN ?')) return;
    try {
      await vpn.delete(configId);
      fetchVpnConfigs();
    } catch (err) {
      const isNetworkError = err.message?.includes('ERR_NETWORK') || err.message?.includes('Failed to fetch');
      if (!isNetworkError) console.error('VPN delete failed:', err);
      toast.error(isNetworkError ? 'Network error, please try again' : 'VPN deletion failed');
    }
  };

  // Load VPN data on mount
  useEffect(() => {
    fetchVpnConfigs();
    fetchVpnStatus();
    loadSavedVpnCredentials();
    const statusInterval = setInterval(fetchVpnStatus, 10000);
    return () => clearInterval(statusInterval);
  }, [fetchVpnConfigs, fetchVpnStatus, loadSavedVpnCredentials]);

  // Load servers when tab is active with real-time refresh
  useEffect(() => {
    if (activeTab === 'servers' && isSuperAdmin) {
      // Initial load with spinner
      fetchServers(true);
      // Real-time silent refresh every 10 seconds
      const serversInterval = setInterval(() => {
        fetchServers(false);
      }, 10000);
      return () => clearInterval(serversInterval);
    }
  }, [activeTab, isSuperAdmin]);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  const sendDemoInvitation = async (e) => {
    e.preventDefault();
    if (!demoInviteForm.email) return;
    setDemoInviteLoading(true);
    setDemoInviteSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/send-demo-invitation`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: demoInviteForm.email,
          first_name: demoInviteForm.firstName || undefined,
          last_name: demoInviteForm.lastName || undefined,
          company_name: demoInviteForm.companyName || undefined,
          message: demoInviteForm.message || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur lors de l'envoi");
      setDemoInviteSuccess(`Invitation sent successfully to ${demoInviteForm.email}`);
      setDemoInviteForm({ email: '', firstName: '', lastName: '', companyName: '', message: '' });
      toast.success(`Demo invitation sent to ${demoInviteForm.email}`);
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'envoi de l'invitation");
    } finally {
      setDemoInviteLoading(false);
    }
  };

  // Fetch server resources (aggregated from all infrastructure servers)
  const fetchServerResources = useCallback(async ({ manual = false } = {}) => {
    // Create abort controller with 10 second timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    if (manual) setIsManualRefreshing(true);

    try {
      // Use aggregated endpoint to get combined stats from all 3 servers
      const response = await fetch(`${API_BASE}/api/v1/system/resources/aggregated`, {
        headers: getAuthHeaders(),
        signal: controller.signal,
      });

      // Handle 401 - token expired, redirect to login
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return;
      }

      if (response.ok) {
        const data = await response.json();
        // Transform API response to match expected frontend format
        const transformed = {
          cpu: {
            usage: data.cpu?.percent || 0,
            cores: data.cpu?.cores || 0,
            physical_cores: data.cpu?.physical_cores || 0,
            model: `${data.cpu?.cores || 0} cores (${data.servers?.online || 0} serveurs)`
          },
          memory: {
            used: data.memory?.used_gb || 0,
            total: data.memory?.total_gb || 0,
            percent: data.memory?.percent || 0,
            available: data.memory?.available_gb || 0
          },
          disk: {
            used: data.disk?.used_gb || 0,
            total: data.disk?.total_gb || 0,
            percent: data.disk?.percent || 0,
            free: data.disk?.free_gb || 0
          },
          gpu: {
            name: data.gpu?.name || 'GPU',
            memory_used: data.gpu?.memory_used_gb || (data.gpu?.memory_used_mb || 0) / 1024,
            memory_total: data.gpu?.memory_total_gb || (data.gpu?.memory_total_mb || 0) / 1024,
            utilization: data.gpu?.percent || 0,
            temperature: data.gpu?.temperature || 0,
            available: data.gpu?.available || false,
            gpus: data.gpu?.gpus || []
          },
          network: {
            rx_bytes: (data.network?.recv_mb || 0) * 1024 * 1024,
            tx_bytes: (data.network?.sent_mb || 0) * 1024 * 1024,
            rx_rate: data.network?.rx_speed_mb || data.network?.rx_rate || 0,
            tx_rate: data.network?.tx_speed_mb || data.network?.tx_rate || 0,
            daily_sent_gb: data.network?.daily_sent_gb || 0,
            daily_recv_gb: data.network?.daily_recv_gb || 0,
            daily_total_gb: data.network?.daily_total_gb || 0
          },
          uptime: data.uptime?.seconds || data.uptime || 0,
          load_average: data.load_average || [0, 0, 0],
          processes: data.processes || 0,
          docker_containers: data.docker?.total || data.docker_containers || 0,
          docker_running: data.docker?.running || 0,
          // New aggregation fields
          servers: data.servers || { total: 1, online: 1, offline: 0 },
          per_server: data.per_server || []
        };
        setServerResources(transformed);
        setResourcesCache(transformed);
      } else {
        // Fallback to single-server endpoint if aggregated fails
        const fallbackResponse = await fetch(`${API_BASE}/api/v1/system/resources`, {
          headers: getAuthHeaders(),
          signal: controller.signal,
        });
        // Handle 401 on fallback too
        if (fallbackResponse.status === 401) {
          localStorage.removeItem('access_token');
          window.location.href = '/login';
          return;
        }
        if (fallbackResponse.ok) {
          const data = await fallbackResponse.json();
          const transformed = {
            cpu: {
              usage: data.cpu?.percent || 0,
              cores: data.cpu?.cores || 0,
              physical_cores: data.cpu?.physical_cores || 0,
              model: data.cpu?.model || 'CPU'
            },
            memory: {
              used: data.memory?.used_gb || 0,
              total: data.memory?.total_gb || 0,
              percent: data.memory?.percent || 0,
              available: data.memory?.available_gb || 0
            },
            disk: {
              used: data.disk?.used_gb || 0,
              total: data.disk?.total_gb || 0,
              percent: data.disk?.percent || 0,
              free: data.disk?.free_gb || 0
            },
            gpu: {
              name: data.gpu?.name || 'GPU',
              memory_used: (data.gpu?.memory_used_mb || 0) / 1024,
              memory_total: (data.gpu?.memory_total_mb || 0) / 1024,
              utilization: data.gpu?.percent || 0,
              temperature: data.gpu?.temperature || 0,
              available: data.gpu?.available || false
            },
            network: {
              rx_bytes: (data.network?.recv_mb || 0) * 1024 * 1024,
              tx_bytes: (data.network?.sent_mb || 0) * 1024 * 1024,
              rx_rate: data.network?.rx_speed_mb || 0,
              tx_rate: data.network?.tx_speed_mb || 0,
              daily_sent_gb: data.network?.daily_sent_gb || 0,
              daily_recv_gb: data.network?.daily_recv_gb || 0,
              daily_total_gb: data.network?.daily_total_gb || 0
            },
            uptime: data.uptime?.seconds || data.uptime || 0,
            load_average: data.load_average || [0, 0, 0],
            processes: data.processes || 0,
            docker_containers: data.docker?.total || 0,
            servers: { total: 1, online: 1, offline: 0 },
            per_server: []
          };
          setServerResources(transformed);
          setResourcesCache(transformed);
        }
      }
    } catch (err) {
      // Silently handle AbortError (timeout) and expected network errors
      if (err.name === 'AbortError') {
        // Request was aborted due to timeout - this is expected, don't log
        return;
      }

      // Silently handle expected network errors (ERR_NETWORK_CHANGED, Failed to fetch, etc.)
      // These are common during network instability (WiFi changes, VPN, etc.)
      const isNetworkError = !err.response && (
        err.message?.includes('Network Error') ||
        err.message?.includes('ERR_NETWORK') ||
        err.message?.includes('ERR_NETWORK_CHANGED') ||
        err.message?.includes('Failed to fetch') ||
        err.name === 'TypeError'
      );

      // Also silently handle 503 Service Unavailable errors (transient server errors)
      const is503Error = err.response?.status === 503;

      // Only log unexpected errors in development (not network or 503 errors)
      if (process.env.NODE_ENV === 'development' && !isNetworkError && !is503Error) {
        console.warn('Failed to fetch resources:', err);
      }
      // Don't overwrite existing data on error - keep current values
      // Only set fallback if no data exists yet
      setServerResources(prev => {
        if (prev) {
          // Keep existing data
          return prev;
        }
        // First load failed, use fallback
        return {
          cpu: { usage: 0, cores: 0, model: 'Chargement...' },
          memory: { used: 0, total: 0, percent: 0 },
          disk: { used: 0, total: 0, percent: 0 },
          gpu: { name: 'Chargement...', memory_used: 0, memory_total: 0, utilization: 0, temperature: 0, available: false },
          network: { rx_bytes: 0, tx_bytes: 0, rx_rate: 0, tx_rate: 0 },
          uptime: 0,
          load_average: [0, 0, 0],
          processes: 0,
          docker_containers: 0,
          servers: { total: 3, online: 0, offline: 3 },
          per_server: []
        };
      });
    } finally {
      clearTimeout(timeoutId);
      setResourcesLoading(false);
      setIsManualRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchServerResources();
    const interval = setInterval(fetchServerResources, 5000);
    return () => clearInterval(interval);
  }, [fetchServerResources]);

  // AI Quick prompts
  const aiQuickPrompts = [
    { icon: Zap, label: 'Optimize performance', query: 'How to optimize server performance with current resources?' },
    { icon: Shield, label: 'Security config', query: 'What security configurations do you recommend?' },
    { icon: Cpu, label: 'GPU & LLM', query: 'How to optimize GPU usage for LLM models?' },
    { icon: Database, label: 'Database', query: 'Recommendations for optimizing PostgreSQL and Redis?' },
  ];

  // AI Assistant query
  const askAI = async (query) => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/ai/analyze`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          query,
          context: {
            server_resources: serverResources,
            type: 'system_optimization'
          }
        }),
      });

      // Handle 401 - token expired, redirect to login
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setAiResponse({
          query,
          response: data.response || data.analysis,
          suggestions: data.suggestions || [],
          optimizations: data.optimizations || [],
          timestamp: new Date().toISOString()
        });
      } else {
        setAiResponse(generateLocalSystemAnalysis(query));
      }
    } catch (err) {
      // Silently handle network errors (ERR_NETWORK_CHANGED, etc.)
      const isNetworkError = !err.response && (
        err.message?.includes('Network Error') ||
        err.message?.includes('ERR_NETWORK') ||
        err.message?.includes('Failed to fetch') ||
        err.name === 'TypeError'
      );
      if (!isNetworkError) {
        console.error('AI query failed:', err);
      }
      setAiResponse(generateLocalSystemAnalysis(query));
    } finally {
      setAiLoading(false);
      setAiQuery('');
    }
  };

  // Local AI analysis for system
  const generateLocalSystemAnalysis = (query) => {
    let response = '';
    let suggestions = [];
    let optimizations = [];
    const lowerQuery = query.toLowerCase();
    const { cpu, memory, disk, gpu } = serverResources;

    if (lowerQuery.includes('performance') || lowerQuery.includes('optimiser')) {
      response = `System Performance Analysis:\n\n`;
      response += `Current status:\n`;
      response += `- CPU: ${cpu.usage?.toFixed(1)}% utilization (${cpu.cores} cores)\n`;
      response += `- RAM: ${memory.percent?.toFixed(1)}% (${memory.used?.toFixed(1)}/${memory.total} GB)\n`;
      response += `- Disk: ${disk.percent?.toFixed(1)}% used\n`;
      response += `- GPU: ${gpu.utilization}% (${gpu.temperature}C)\n\n`;

      if (cpu.usage > 80) {
        response += `ALERT: CPU overloaded!
`;
        optimizations.push({ id: 'cpu_limit', name: 'Limit concurrent scans', action: 'reduce_concurrent_scans' });
      }
      if (memory.percent > 85) {
        response += `ALERT: Critical memory!
`;
        optimizations.push({ id: 'mem_clear', name: 'Clear cache', action: 'clear_cache' });
      }

      response += `\nRecommandations:\n`;
      response += `1. Configure max_concurrent_scans = ${Math.floor(cpu.cores / 2)}\n`;
      response += `2. Enable swap if RAM > 80%
`;
      response += `3. Schedule heavy scans during off-peak hours`;
      suggestions = ['View GPU config', 'Optimize Docker', 'Clean logs'];
    } else if (lowerQuery.includes('securite') || lowerQuery.includes('security')) {
      response = `Security Recommendations:\n\n`;
      response += `1. Authentication:\n`;
      response += `   - Enable 2FA for all accounts\n`;
      response += `   - JWT token rotation (24h max)\n`;
      response += `   - Strong password policy\n\n`;
      response += `2. Network:\n`;
      response += `   - Firewall: limit exposed ports\n`;
      response += `   - HTTPS required\n`;
      response += `   - API rate limiting\n\n`;
      response += `3. Data:\n`;
      response += `   - Database encryption\n`;
      response += `   - Daily backup\n`;
      response += `   - Log retention 90 days`;
      suggestions = ['Configure 2FA', 'View firewall', 'Enable backup'];
      optimizations.push({ id: 'enable_2fa', name: 'Enable 2FA', action: 'enable_2fa' });
    } else if (lowerQuery.includes('gpu') || lowerQuery.includes('llm')) {
      response = `GPU/LLM Optimization:\n\n`;
      response += `GPU detected: ${gpu.name || 'Not detected'}\n`;
      response += `VRAM: ${gpu.memory_used?.toFixed(1)}/${gpu.memory_total} GB\n`;
      response += `Utilization: ${gpu.utilization}%\n`;
      response += `Temperature: ${gpu.temperature}C\n\n`;

      response += `Recommendations:\n`;
      response += `1. Recommended models by VRAM:\n`;
      if (gpu.memory_total >= 24) {
        response += `   - Llama 70B (Q4) - Full capacity\n`;
      } else if (gpu.memory_total >= 16) {
        response += `   - Llama 13B - Optimal\n`;
        response += `   - CodeLlama 34B (Q4) - Possible\n`;
      } else if (gpu.memory_total >= 8) {
        response += `   - Llama 7B - Recommended\n`;
        response += `   - Mistral 7B - Optimal\n`;
      } else {
        response += `   - TinyLlama - Light models\n`;
      }
      response += `\n2. Optimal batch size: ${Math.floor(gpu.memory_total / 2)}\n`;
      response += `3. Enable Flash Attention 2 if available`;
      suggestions = ['Change model', 'View CUDA config', 'Test inference'];
    } else if (lowerQuery.includes('database') || lowerQuery.includes('postgresql') || lowerQuery.includes('redis')) {
      response = `Database Optimization:\n\n`;
      response += `PostgreSQL:\n`;
      response += `- shared_buffers: ${Math.floor(memory.total / 4)}GB recommended\n`;
      response += `- effective_cache_size: ${Math.floor(memory.total * 0.75)}GB\n`;
      response += `- work_mem: ${Math.floor(memory.total * 1024 / (cpu.cores * 4))}MB\n`;
      response += `- maintenance_work_mem: ${Math.floor(memory.total * 1024 / 16)}MB\n\n`;
      response += `Redis:\n`;
      response += `- maxmemory: ${Math.floor(memory.total / 8)}GB\n`;
      response += `- maxmemory-policy: allkeys-lru\n`;
      response += `- Enable AOF persistence`;
      suggestions = ['Apply config', 'Vacuum DB', 'View connections'];
      optimizations.push({ id: 'tune_pg', name: 'Optimize PostgreSQL', action: 'tune_postgresql' });
    } else {
      response = `SYLINK System Analysis:\n\n`;
      response += `Server resources:\n`;
      response += `- CPU: ${cpu.usage?.toFixed(1)}% (${cpu.cores} cores)\n`;
      response += `- RAM: ${memory.used?.toFixed(1)}/${memory.total} GB (${memory.percent?.toFixed(1)}%)\n`;
      response += `- Disk: ${disk.used}/${disk.total} GB (${disk.percent?.toFixed(1)}%)\n`;
      response += `- GPU: ${gpu.name || 'N/A'}\n`;
      response += `- Uptime: ${Math.floor(serverResources.uptime / 86400)}d ${Math.floor((serverResources.uptime % 86400) / 3600)}h\n`;
      response += `- Containers Docker: ${serverResources.docker_containers}\n\n`;
      response += `Overall status: ${cpu.usage < 70 && memory.percent < 80 ? 'HEALTHY' : 'REQUIRES ATTENTION'}`;
      suggestions = ['Optimize performance', 'Security config', 'GPU & LLM'];
    }

    return {
      query,
      response,
      suggestions,
      optimizations,
      timestamp: new Date().toISOString(),
      isLocal: true
    };
  };

  // Generate optimization suggestions based on resources
  useEffect(() => {
    const suggestions = [];
    const { cpu, memory, disk, gpu } = serverResources;

    if (cpu.usage > 80) {
      suggestions.push({
        id: 'cpu_high',
        type: 'warning',
        title: 'CPU overloaded',
        description: 'Reduce concurrent scans',
        action: 'reduce_scans'
      });
    }
    if (memory.percent > 85) {
      suggestions.push({
        id: 'mem_high',
        type: 'critical',
        title: 'Critical memory',
        description: 'Clear cache and restart services',
        action: 'clear_cache'
      });
    }
    if (disk.percent > 90) {
      suggestions.push({
        id: 'disk_high',
        type: 'critical',
        title: 'Espace disque faible',
        description: 'Supprimer anciens scans et logs',
        action: 'clean_disk'
      });
    }
    if (gpu.temperature > 80) {
      suggestions.push({
        id: 'gpu_hot',
        type: 'warning',
        title: 'GPU surchauffe',
        description: 'Reduce LLM batch size',
        action: 'reduce_batch'
      });
    }

    setOptimizationSuggestions(suggestions);
  }, [serverResources]);

  // Apply optimization
  const applyOptimization = async (optimization) => {
    setApplyingOptimization(optimization.id);
    try {
      // Simulate optimization
      await new Promise(r => setTimeout(r, 2000));
      alert(`Optimisation "${optimization.title}" applied successfully!`);
    } catch (err) {
      alert('Erreur lors de l\'application');
    } finally {
      setApplyingOptimization(null);
    }
  };

  const handleSaveIntegration = (type, config) => {
    setIntegrations({
      ...integrations,
      [type]: { configured: true, ...config }
    });
  };

  const formatBytes = (bytes) => {
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(2) + ' GB';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(2) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return bytes + ' B';
  };

  const formatUptime = (seconds) => {
    // Handle undefined, null, NaN or invalid values
    if (!seconds || isNaN(seconds) || seconds <= 0) {
      return '0j 0h 0m';
    }
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${days}j ${hours}h ${mins}m`;
  };

  const getUsageColor = (percent) => {
    const value = percent || 0;
    if (value > 90) return 'text-red-500';
    if (value > 70) return 'text-orange-500';
    if (value > 50) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getUsageBgColor = (percent) => {
    const value = percent || 0;
    if (value > 90) return 'bg-red-500';
    if (value > 70) return 'bg-orange-500';
    if (value > 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Safe number formatting helper
  const safeFixed = (value, decimals = 1) => {
    if (value === undefined || value === null || isNaN(value)) return '0';
    return Number(value).toFixed(decimals);
  };

  // Tabs organized by category - using useMemo to recalculate when isSuperAdmin changes
  const tabCategories = useMemo(() => [
    {
      category: 'System',
      icon: <Server className="w-4 h-4" />,
      tabs: [
        { id: 'general', name: t('settings.general'), icon: <SettingsIcon className="w-5 h-5" /> },
        { id: 'resources', name: 'Ressources', icon: <Server className="w-5 h-5" /> },
        // Servers/VPS Monitoring - Superadmin only
        ...(isSuperAdmin ? [{ id: 'servers', name: 'Serveurs', icon: <Database className="w-5 h-5" />, superadminOnly: true }] : []),
        { id: 'license', name: 'Licence', icon: <Key className="w-5 h-5" /> },
        { id: 'logs', name: 'Logs', icon: <FileText className="w-5 h-5" /> },
        { id: 'updates', name: 'Updates', icon: <RefreshCw className="w-5 h-5" /> },
        // Tracking Logs - Superadmin only
        ...(isSuperAdmin ? [{ id: 'tracking', name: 'Journaux de Suivi', icon: <History className="w-5 h-5" />, superadminOnly: true }] : []),
        // Audit Logs (API) - Superadmin only
        ...(isSuperAdmin ? [{ id: 'audit', name: 'Journaux API', icon: <Database className="w-5 h-5" />, superadminOnly: true }] : []),
        // Clients Management - Superadmin only
        ...(isSuperAdmin ? [{ id: 'clients', name: 'Clients', icon: <Users className="w-5 h-5" />, superadminOnly: true }] : []),
      ]
    },
    {
      category: 'Security',
      icon: <Shield className="w-4 h-4" />,
      tabs: [
        { id: 'security', name: 'Authentification', icon: <Shield className="w-5 h-5" /> },
        { id: 'vpn', name: 'VPN', icon: <Globe className="w-5 h-5" /> },
        { id: 'proxy', name: 'Proxy', icon: <Network className="w-5 h-5" /> },
      ]
    },
    {
      category: 'Intelligence Artificielle',
      icon: <Brain className="w-4 h-4" />,
      tabs: [
        { id: 'orchestrator', name: 'Orchestrateur', icon: <Activity className="w-5 h-5" /> },
        { id: 'ai', name: 'AI Reasoning', icon: <Brain className="w-5 h-5" /> },
        { id: 'gpu', name: 'GPU Acceleration', icon: <Cpu className="w-5 h-5" /> },
        { id: 'attack-matrix', name: 'Matrice Attaques', icon: <Grid3x3 className="w-5 h-5" /> },
        { id: 'llm-logic', name: 'Logique LLM', icon: <GitBranch className="w-5 h-5" /> },
        // AI Agent Supervisor - Superadmin only
        ...(isSuperAdmin ? [{ id: 'ai-agent', name: 'Agent IA', icon: <Bot className="w-5 h-5" />, superadminOnly: true }] : []),
        // AI Assistant Settings - Superadmin only
        ...(isSuperAdmin ? [{ id: 'ai-assistant', name: 'Assistant IA', icon: <Sparkles className="w-5 h-5" />, superadminOnly: true }] : []),
      ]
    },
    {
      category: 'Pentest',
      icon: <Wrench className="w-4 h-4" />,
      tabs: [
        { id: 'tools', name: t('settings.tools'), icon: <Wrench className="w-5 h-5" /> },
        { id: 'orchestrator-tools', name: 'Outils Orchestrateur', icon: <Activity className="w-5 h-5" /> },
        { id: 'modules', name: 'Modules', icon: <Package className="w-5 h-5" /> },
        { id: 'connectors', name: 'Connecteurs OSINT', icon: <Shield className="w-5 h-5" /> },
        { id: 'reports', name: 'Rapports', icon: <FileText className="w-5 h-5" /> },
        { id: 'contract', name: 'Contrat Client', icon: <PenTool className="w-5 h-5" /> },
      ]
    },
    {
      category: 'Danger',
      icon: <AlertCircle className="w-4 h-4" />,
      tabs: [
        { id: 'reset', name: 'Reset', icon: <Trash2 className="w-5 h-5 text-red-400" />, danger: true },
      ]
    },
  ], [isSuperAdmin, t]);

  // Flat tabs array for backwards compatibility - also memoized
  const tabs = useMemo(() => tabCategories.flatMap(cat => cat.tabs), [tabCategories]);

  const integrationsList = [
    { id: 'email', name: 'Email SMTP', description: 'Notifications par email', icon: '📧', wizard: 'email', category: 'Notifications' },
    { id: 'splunk', name: 'Splunk SIEM', description: 'SIEM', icon: '📡', wizard: 'splunk', category: 'SIEM' },
    { id: 'jira', name: 'Jira', description: 'Gestion des tickets', icon: '🎫', wizard: 'jira', category: 'Ticketing' },
    { id: 'elasticsearch', name: 'Elasticsearch', description: 'Recherche', icon: '🔍', wizard: null, category: 'Analytics' },
    { id: 'slack', name: 'Slack', description: 'Notifications', icon: '💬', wizard: null, category: 'ChatOps' },
    { id: 'teams', name: 'Teams', description: 'Collaboration', icon: '💼', wizard: 'teams', category: 'ChatOps' },
    { id: 'discord', name: 'Discord', description: 'Community', icon: '🎮', wizard: 'discord', category: 'ChatOps' },
    { id: 'prometheus', name: 'Prometheus', description: 'Monitoring', icon: '📊', wizard: null, category: 'Monitoring' },
  ];

  // Show loading state while fetching user data
  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-brand-black p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green mx-auto mb-4"></div>
          <p className="text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
        {/* Header with AI Assistant Toggle */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gradient-green">{t('settings.title')}</h1>
            <p className="text-gray-400 mt-2">Configurez la plateforme avec l'assistant IA et le monitoring des ressources</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAIAssistant(!showAIAssistant)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showAIAssistant ? 'bg-blue-500 text-white' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
              }`}
            >
              <Brain className="w-4 h-4" />
              Assistant IA
            </button>
            <button
              onClick={() => fetchServerResources({ manual: true })}
              disabled={isManualRefreshing}
              className="px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isManualRefreshing ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </button>
          </div>
        </div>

        {/* Server Resources Overview - Always visible */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <div className="bg-brand-black-light rounded-xl p-3 border border-brand-green/20 overflow-hidden">
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <Cpu className="w-4 h-4 text-brand-green flex-shrink-0" />
              <span className="text-sm text-gray-400 font-medium truncate">CPU</span>
            </div>
            {resourcesLoading ? (
              <>
                <div className="h-7 bg-gray-600/70 rounded animate-pulse mb-1"></div>
                <div className="h-1.5 bg-gray-600/70 rounded animate-pulse mt-1"></div>
                <div className="h-3 w-16 bg-gray-600/70 rounded animate-pulse mt-1"></div>
              </>
            ) : serverResources?.cpu?.cores > 0 ? (
              <>
                <div className={`text-xl font-bold whitespace-nowrap ${getUsageColor(serverResources?.cpu?.usage || 0)}`}>
                  {safeFixed(serverResources?.cpu?.usage || 0)}%
                </div>
                <div className="w-full h-1.5 bg-gray-700 rounded mt-1">
                  <div className={`h-full rounded ${getUsageBgColor(serverResources?.cpu?.usage || 0)}`} style={{ width: `${serverResources?.cpu?.usage || 0}%` }} />
                </div>
                <div className="text-xs text-gray-500 mt-1">{serverResources?.cpu?.cores || 0} cores</div>
              </>
            ) : (
              <>
                <div className="text-xl font-bold text-gray-500">N/D</div>
                <div className="w-full h-1.5 bg-gray-700 rounded mt-1"></div>
                <div className="text-xs text-gray-600 mt-1">Serveurs hors ligne</div>
              </>
            )}
          </div>

          <div className="bg-brand-black-light rounded-xl p-3 border border-blue-500/20 overflow-hidden">
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <MemoryStick className="w-4 h-4 text-blue-400 flex-shrink-0" />
              <span className="text-sm text-gray-400 font-medium truncate">RAM</span>
            </div>
            {resourcesLoading ? (
              <>
                <div className="h-7 bg-gray-600/70 rounded animate-pulse mb-1"></div>
                <div className="h-3 w-20 bg-gray-600/70 rounded animate-pulse"></div>
              </>
            ) : serverResources?.memory?.total > 0 ? (
              <>
                <div className={`text-xl font-bold whitespace-nowrap ${getUsageColor(serverResources?.memory?.percent || 0)}`}>
                  {safeFixed(serverResources?.memory?.percent || 0)}%
                </div>
                <div className="text-xs text-gray-500 truncate">{safeFixed(serverResources?.memory?.used || 0)}GB / {safeFixed(serverResources?.memory?.total || 0)}GB</div>
              </>
            ) : (
              <>
                <div className="text-xl font-bold text-gray-500">N/D</div>
                <div className="text-xs text-gray-600">Serveurs hors ligne</div>
              </>
            )}
          </div>

          <div className="bg-brand-black-light rounded-xl p-3 border border-purple-500/20 overflow-hidden">
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <HardDrive className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span className="text-sm text-gray-400 font-medium truncate">Disque</span>
            </div>
            {resourcesLoading ? (
              <>
                <div className="h-7 bg-gray-600/70 rounded animate-pulse mb-1"></div>
                <div className="h-3 w-20 bg-gray-600/70 rounded animate-pulse"></div>
              </>
            ) : serverResources?.disk?.total > 0 ? (
              <>
                <div className={`text-xl font-bold whitespace-nowrap ${getUsageColor(serverResources?.disk?.percent || 0)}`}>
                  {safeFixed(serverResources?.disk?.percent || 0)}%
                </div>
                <div className="text-xs text-gray-500 truncate">{safeFixed(serverResources?.disk?.used || 0)}GB / {safeFixed(serverResources?.disk?.total || 0)}GB</div>
              </>
            ) : (
              <>
                <div className="text-xl font-bold text-gray-500">N/D</div>
                <div className="text-xs text-gray-600">Serveurs hors ligne</div>
              </>
            )}
          </div>

          <div className="bg-brand-black-light rounded-xl p-3 border border-green-500/20 overflow-hidden">
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <Gauge className="w-4 h-4 text-green-400 flex-shrink-0" />
              <span className="text-sm text-gray-400 font-medium truncate">GPU</span>
            </div>
            {resourcesLoading ? (
              <>
                <div className="h-7 bg-gray-600/70 rounded animate-pulse mb-1"></div>
                <div className="h-3 w-12 bg-gray-600/70 rounded animate-pulse"></div>
              </>
            ) : (
              <>
                <div className={`text-xl font-bold whitespace-nowrap ${getUsageColor(serverResources?.gpu?.utilization || 0)}`}>
                  {serverResources?.gpu?.utilization || 0}%
                </div>
                <div className="text-xs text-gray-500">{serverResources?.gpu?.temperature || 0}°C</div>
              </>
            )}
          </div>

          <div className="bg-brand-black-light rounded-xl p-3 border border-yellow-500/20 overflow-hidden">
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <Thermometer className="w-4 h-4 text-yellow-400 flex-shrink-0" />
              <span className="text-sm text-gray-400 font-medium truncate">GPU Mem.</span>
            </div>
            {resourcesLoading ? (
              <>
                <div className="h-7 bg-gray-600/70 rounded animate-pulse mb-1"></div>
                <div className="h-3 w-14 bg-gray-600/70 rounded animate-pulse"></div>
              </>
            ) : (
              <>
                <div className="text-xl font-bold text-yellow-400 whitespace-nowrap">
                  {safeFixed(serverResources?.gpu?.memory_used || 0)}GB
                </div>
                <div className="text-xs text-gray-500">/{safeFixed(serverResources?.gpu?.memory_total || 0)}GB</div>
              </>
            )}
          </div>

          <div className="bg-brand-black-light rounded-xl p-3 border border-cyan-500/20 overflow-hidden">
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <Network className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              <span className="text-sm text-gray-400 font-medium truncate">Network</span>
            </div>
            {resourcesLoading ? (
              <>
                <div className="h-5 bg-gray-600/70 rounded animate-pulse mb-1"></div>
                <div className="h-3 w-20 bg-gray-600/70 rounded animate-pulse"></div>
              </>
            ) : (
              <>
                <div className="text-sm font-bold whitespace-nowrap truncate">
                  <span className="text-cyan-400">↓{safeFixed((serverResources?.network?.rx_bytes || 0) / 1073741824, 1)}GB</span>
                  <span className="text-gray-500 mx-1">/</span>
                  <span className="text-orange-400">↑{safeFixed((serverResources?.network?.tx_bytes || 0) / 1073741824, 1)}GB</span>
                </div>
                <div className="text-xs text-gray-500">Depuis uptime</div>
              </>
            )}
          </div>

          <div className="bg-brand-black-light rounded-xl p-3 border border-orange-500/20 overflow-hidden">
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <Timer className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span className="text-sm text-gray-400 font-medium truncate">{t('settings.uptime')}</span>
            </div>
            {resourcesLoading ? (
              <div className="h-6 bg-gray-600/70 rounded animate-pulse"></div>
            ) : (
              <div className="text-lg font-bold text-orange-400 whitespace-nowrap truncate">
                {formatUptime(serverResources?.uptime || 0)}
              </div>
            )}
          </div>

          <div className="bg-brand-black-light rounded-xl p-3 border border-pink-500/20 overflow-hidden">
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <Package className="w-4 h-4 text-pink-400 flex-shrink-0" />
              <span className="text-sm text-gray-400 font-medium truncate">Docker</span>
            </div>
            {resourcesLoading ? (
              <>
                <div className="h-7 bg-gray-600/70 rounded animate-pulse mb-1"></div>
                <div className="h-3 w-16 bg-gray-600/70 rounded animate-pulse"></div>
              </>
            ) : (
              <>
                <div className="text-xl font-bold text-pink-400 whitespace-nowrap">
                  {serverResources?.docker_containers || 0}
                </div>
                <div className="text-xs text-gray-500">conteneurs</div>
              </>
            )}
          </div>
        </div>

        {/* Optimization Alerts */}
        {optimizationSuggestions.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <h3 className="text-lg font-bold text-yellow-400 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Suggestions d'Optimisation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {optimizationSuggestions.map(opt => (
                <div key={opt.id} className={`p-3 rounded-lg border ${
                  opt.type === 'critical' ? 'border-red-500/50 bg-red-500/10' : 'border-yellow-500/50 bg-yellow-500/10'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${opt.type === 'critical' ? 'text-red-400' : 'text-yellow-400'}`}>
                      {opt.title}
                    </span>
                    {opt.type === 'critical' && <AlertCircle className="w-4 h-4 text-red-400" />}
                  </div>
                  <p className="text-xs text-gray-400 mb-2 break-words">{opt.description}</p>
                  <button
                    onClick={() => applyOptimization(opt)}
                    disabled={applyingOptimization === opt.id}
                    className="w-full px-2 py-1 bg-brand-green/20 text-brand-green rounded text-xs hover:bg-brand-green/30 flex items-center justify-center gap-1"
                  >
                    {applyingOptimization === opt.id ? (
                      <RefreshCw className="w-3 h-3 animate-spin" />
                    ) : (
                      <Zap className="w-3 h-3" />
                    )}
                    Appliquer
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Assistant Panel */}
        {showAIAssistant && (
          <div className="bg-brand-black-light rounded-xl border border-blue-500/30 overflow-hidden">
            <div className="p-4 border-b border-blue-500/20 bg-blue-500/10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-blue-400" />
                AI Assistant - System Optimization
                <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">LLM</span>
              </h3>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                {aiQuickPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => askAI(prompt.query)}
                    disabled={aiLoading}
                    className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors flex items-center gap-2 text-sm"
                  >
                    <prompt.icon className="w-4 h-4" />
                    {prompt.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && aiQuery && askAI(aiQuery)}
                  placeholder="Ask a question about configuration, performance, security..."
                  className="flex-1 px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500"
                />
                <button
                  onClick={() => aiQuery && askAI(aiQuery)}
                  disabled={aiLoading || !aiQuery}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Analyser
                </button>
              </div>

              {aiResponse && (
                <div className="bg-brand-black rounded-lg p-4 border border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-400">AI Response</span>
                        {aiResponse.isLocal && <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">Local</span>}
                      </div>
                      <MarkdownRenderer content={aiResponse.response} variant="compact" />

                      {aiResponse.optimizations && aiResponse.optimizations.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-xs text-gray-500 mb-2">Optimisations applicables:</div>
                          <div className="flex flex-wrap gap-2">
                            {aiResponse.optimizations.map((opt, i) => (
                              <button
                                key={i}
                                onClick={() => applyOptimization({ id: opt.id, title: opt.name })}
                                className="px-2 py-1 bg-brand-green/10 text-brand-green rounded text-xs hover:bg-brand-green/20 flex items-center gap-1"
                              >
                                <Zap className="w-3 h-3" />
                                {opt.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {aiResponse.suggestions && aiResponse.suggestions.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-xs text-gray-500 mb-2">Suggestions:</div>
                          <div className="flex flex-wrap gap-2">
                            {aiResponse.suggestions.map((s, i) => (
                              <button key={i} onClick={() => askAI(s)} className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs hover:bg-blue-500/20 flex items-center gap-1">
                                <Lightbulb className="w-3 h-3" />{s}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-4 space-y-1 sticky top-6">
              {tabCategories.map((category, catIndex) => (
                <div key={category.category} className={catIndex > 0 ? 'pt-3' : ''}>
                  {/* Category Header */}
                  <div
                    className={`flex items-center gap-2 px-3 py-2 mb-1 min-w-0 ${
                      category.category === 'Danger' ? 'text-red-400/70' : 'text-gray-500'
                    }`}
                    title={category.category}
                  >
                    <span className="flex-shrink-0">{category.icon}</span>
                    <span className="text-xs font-semibold uppercase tracking-wider break-words">{category.category}</span>
                  </div>
                  {/* Category Tabs */}
                  <div className="space-y-1">
                    {category.tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        title={tab.name}
                        className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center gap-3 border-2 min-w-0 ${
                          activeTab === tab.id
                            ? tab.danger
                              ? 'bg-red-500/20 text-red-400 border-red-500 font-semibold'
                              : 'bg-brand-green text-brand-black border-brand-green font-semibold'
                            : tab.danger
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-950/30 border-transparent hover:border-red-500/30'
                              : 'text-gray-400 hover:text-brand-green hover:bg-brand-black-lighter border-transparent hover:border-brand-green/30'
                        }`}
                      >
                        <span className="flex-shrink-0">{tab.icon}</span>
                        <span className="text-sm break-words">{tab.name}</span>
                      </button>
                    ))}
                  </div>
                  {/* Separator line (except for last category) */}
                  {catIndex < tabCategories.length - 1 && (
                    <div className="mt-3 border-b border-gray-800"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">

              {/* General Tab */}
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">{t('settings.generalSection.title')}</h2>
                    <p className="text-gray-400">Complete SYLINK system details</p>
                  </div>
                  <SystemInfo />
                </div>
              )}

              {/* Resources Tab - New */}
              {activeTab === 'resources' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Ressources Serveur</h2>
                    <p className="text-gray-400">Monitoring en temps reel et optimisation automatique</p>
                  </div>

                  {/* Detailed Resource Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* CPU Details */}
                    <div className="bg-brand-black rounded-xl p-4 border border-brand-green/20">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-brand-green/20 flex items-center justify-center">
                          <Cpu className="w-6 h-6 text-brand-green" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">CPU</h3>
                          <p className="text-sm text-gray-500">{serverResources.cpu.model}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Utilisation</span>
                          <span className={`font-bold ${getUsageColor(serverResources.cpu.usage)}`}>
                            {safeFixed(serverResources.cpu.usage)}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-700 rounded">
                          <div className={`h-full rounded ${getUsageBgColor(serverResources.cpu.usage)}`} style={{ width: `${serverResources.cpu.usage || 0}%` }} />
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Coeurs: {serverResources.cpu.cores || 0}</span>
                          <span className="text-gray-500">Load: {(serverResources.load_average || [0, 0, 0]).map(l => (l || 0).toFixed(2)).join(' ')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Memory Details */}
                    <div className="bg-brand-black rounded-xl p-4 border border-blue-500/20">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <MemoryStick className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">Memoire RAM</h3>
                          <p className="text-sm text-gray-500">{serverResources.memory.total} GB Total</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Utilisee</span>
                          <span className={`font-bold ${getUsageColor(serverResources.memory.percent)}`}>
                            {safeFixed(serverResources.memory.used)} GB ({safeFixed(serverResources.memory.percent)}%)
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-700 rounded">
                          <div className={`h-full rounded ${getUsageBgColor(serverResources.memory.percent)}`} style={{ width: `${serverResources.memory.percent || 0}%` }} />
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Libre: {safeFixed((serverResources.memory.total || 0) - (serverResources.memory.used || 0))} GB</span>
                          <span className="text-gray-500">Processus: {serverResources.processes || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* GPU Details */}
                    <div className="bg-brand-black rounded-xl p-4 border border-green-500/20">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                          <Gauge className="w-6 h-6 text-green-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">GPU</h3>
                          <p className="text-sm text-gray-500">{serverResources.gpu.name}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Utilisation</span>
                          <span className={`font-bold ${getUsageColor(serverResources.gpu.utilization)}`}>
                            {serverResources.gpu.utilization || 0}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-700 rounded">
                          <div className={`h-full rounded ${getUsageBgColor(serverResources.gpu.utilization)}`} style={{ width: `${serverResources.gpu.utilization || 0}%` }} />
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">VRAM: {safeFixed(serverResources.gpu.memory_used)}/{serverResources.gpu.memory_total || 0} GB</span>
                          <span className={`${(serverResources.gpu.temperature || 0) > 75 ? 'text-red-400' : 'text-gray-500'}`}>
                            Temp: {serverResources.gpu.temperature || 0}°C
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Disk Details */}
                    <div className="bg-brand-black rounded-xl p-4 border border-purple-500/20">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <HardDrive className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">Stockage</h3>
                          <p className="text-sm text-gray-500">{serverResources.disk.total} GB Total</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Utilise</span>
                          <span className={`font-bold ${getUsageColor(serverResources.disk.percent)}`}>
                            {serverResources.disk.used || 0} GB ({safeFixed(serverResources.disk.percent)}%)
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-700 rounded">
                          <div className={`h-full rounded ${getUsageBgColor(serverResources.disk.percent)}`} style={{ width: `${serverResources.disk.percent || 0}%` }} />
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Libre: {(serverResources.disk.total || 0) - (serverResources.disk.used || 0)} GB</span>
                          <span className="text-gray-500">Docker: {serverResources.docker_containers || 0} conteneurs</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Network & System Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-brand-black rounded-lg p-4 border border-cyan-500/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Download className="w-5 h-5 text-cyan-400" />
                        <span className="text-white font-medium">Network RX (Today)</span>
                      </div>
                      <div className="text-2xl font-bold text-cyan-400">{safeFixed(serverResources.network.daily_recv_gb || 0, 2)} GB</div>
                      <div className="text-xs text-gray-500">Vitesse: {safeFixed(serverResources.network.rx_rate, 2)} MB/s</div>
                    </div>
                    <div className="bg-brand-black rounded-lg p-4 border border-orange-500/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Upload className="w-5 h-5 text-orange-400" />
                        <span className="text-white font-medium">Network TX (Today)</span>
                      </div>
                      <div className="text-2xl font-bold text-orange-400">{safeFixed(serverResources.network.daily_sent_gb || 0, 2)} GB</div>
                      <div className="text-xs text-gray-500">Vitesse: {safeFixed(serverResources.network.tx_rate, 2)} MB/s</div>
                    </div>
                    <div className="bg-brand-black rounded-lg p-4 border border-yellow-500/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Timer className="w-5 h-5 text-yellow-400" />
                        <span className="text-white font-medium">{t('settings.uptime')}</span>
                      </div>
                      <div className="text-2xl font-bold text-yellow-400">{formatUptime(serverResources.uptime)}</div>
                      <div className="text-xs text-gray-500">{serverResources.processes || 0} processus actifs</div>
                    </div>
                  </div>

                  {/* AI Recommendations based on resources */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Brain className="w-5 h-5 text-blue-400" />
                      <span className="text-blue-400 font-bold">Recommandations IA</span>
                    </div>
                    <div className="text-sm text-gray-300">
                      {serverResources.cpu.usage < 50 && serverResources.memory.percent < 60 ? (
                        <p>Ressources optimales - Vous pouvez augmenter le nombre de scans concurrents.</p>
                      ) : serverResources.cpu.usage > 80 || serverResources.memory.percent > 85 ? (
                        <p>High load detected - Consider reducing concurrent operations or increasing resources.</p>
                      ) : (
                        <p>System operating normally. Monitor GPU memory for intensive LLM operations.</p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowAIAssistant(true);
                        askAI('Analyse complete des ressources et optimisations recommandees');
                      }}
                      className="mt-3 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 flex items-center gap-2 text-sm"
                    >
                      <Sparkles className="w-4 h-4" />
                      Detailed AI analysis
                    </button>
                  </div>

                  {/* Resource Charts with History */}
                  <div className="mt-6">
                    <ResourceCharts />
                  </div>
                </div>
              )}

              {/* Servers Tab - Multi-server/VPS Monitoring */}
              {activeTab === 'servers' && isSuperAdmin && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-brand-green mb-2">Serveurs & VPS</h2>
                      <p className="text-gray-400">Monitoring multi-serveurs de l'infrastructure</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={refreshAllServers}
                        disabled={serversRefreshing}
                        className="px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 flex items-center gap-2 disabled:opacity-50"
                      >
                        <RefreshCw className={`w-4 h-4 ${serversRefreshing ? 'animate-spin' : ''}`} />
                        {t('common.refresh')}
                      </button>
                      <button
                        onClick={() => setAddServerModal(true)}
                        className="px-4 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/80 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        {t('common.add')}
                      </button>
                    </div>
                  </div>

                  {/* Stats Overview */}
                  {serversStats && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      <div className="bg-brand-black rounded-lg p-4 border border-brand-green/20">
                        <div className="text-3xl font-bold text-brand-green">{serversStats.total_servers}</div>
                        <div className="text-sm text-gray-400">Total serveurs</div>
                      </div>
                      <div className="bg-brand-black rounded-lg p-4 border border-green-500/20">
                        <div className="text-3xl font-bold text-green-400">{serversStats.online}</div>
                        <div className="text-sm text-gray-400">En ligne</div>
                      </div>
                      <div className="bg-brand-black rounded-lg p-4 border border-red-500/20">
                        <div className="text-3xl font-bold text-red-400">{serversStats.offline}</div>
                        <div className="text-sm text-gray-400">Hors ligne</div>
                      </div>
                      <div className="bg-brand-black rounded-lg p-4 border border-yellow-500/20">
                        <div className="text-3xl font-bold text-yellow-400">{serversStats.degraded}</div>
                        <div className="text-sm text-gray-400">Degraded</div>
                      </div>
                      <div className="bg-brand-black rounded-lg p-4 border border-gray-500/20">
                        <div className="text-3xl font-bold text-gray-400">{serversStats.unknown || 0}</div>
                        <div className="text-sm text-gray-400">Inconnu</div>
                      </div>
                      <div className="bg-brand-black rounded-lg p-4 border border-purple-500/20">
                        <div className="text-3xl font-bold text-purple-400">{serversStats.inactive || 0}</div>
                        <div className="text-sm text-gray-400">Inactifs</div>
                      </div>
                    </div>
                  )}

                  {/* Servers List */}
                  {serversLoading ? (
                    <div className="text-center py-12">
                      <RefreshCw className="w-8 h-8 text-brand-green animate-spin mx-auto mb-4" />
                      <p className="text-gray-400">{t('common.loading')}</p>
                    </div>
                  ) : serversList.length === 0 ? (
                    <div className="text-center py-12 bg-brand-black rounded-xl border border-brand-green/20">
                      <Database className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-white mb-2">No server configured</h3>
                      <p className="text-gray-400 mb-4">Ajoutez vos serveurs et VPS pour les monitorer</p>
                      <button
                        onClick={() => setAddServerModal(true)}
                        className="px-6 py-3 bg-brand-green text-black rounded-lg hover:bg-brand-green/80 flex items-center gap-2 mx-auto"
                      >
                        <Plus className="w-5 h-5" />
                        {t('common.add')}
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {serversList.map((server) => (
                        <div
                          key={server.id}
                          className={`bg-brand-black rounded-xl p-4 border ${
                            server.status === 'online' ? 'border-green-500/30' :
                            server.status === 'offline' ? 'border-red-500/30' :
                            server.status === 'degraded' ? 'border-yellow-500/30' :
                            'border-gray-500/30'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                server.status === 'online' ? 'bg-green-500/20' :
                                server.status === 'offline' ? 'bg-red-500/20' :
                                'bg-yellow-500/20'
                              }`}>
                                <Server className={`w-5 h-5 ${
                                  server.status === 'online' ? 'text-green-400' :
                                  server.status === 'offline' ? 'text-red-400' :
                                  'text-yellow-400'
                                }`} />
                              </div>
                              <div>
                                <h3 className="font-bold text-white">{server.name}</h3>
                                <p className="text-sm text-gray-500">{server.ip_address}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setServerInfoModal(server)}
                                className="p-1.5 text-gray-400 hover:text-blue-400 rounded"
                                title={t('common.info')}
                              >
                                <Info className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => refreshServer(server.id)}
                                className="p-1.5 text-gray-400 hover:text-brand-green rounded"
                                title={t('common.refresh')}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteServer(server.id)}
                                className="p-1.5 text-gray-400 hover:text-red-400 rounded"
                                title={t('common.delete')}
                              >
                                <Trash className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Show metrics for all servers that have cached data */}
                          {server.metrics && (server.metrics.memory?.total_gb > 0 || server.metrics.disk?.total_gb > 0) && (
                            <div className="space-y-2">
                              {/* Status indicator for non-online servers */}
                              {server.status !== 'online' && (
                                <div className="text-xs text-yellow-400 mb-2 flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></span>
                                  {server.status === 'offline' ? 'Offline - Last known data' : 'Unknown status - Cached data'}
                                </div>
                              )}
                              {/* CPU */}
                              <div>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-400">CPU</span>
                                  <span className={server.metrics.cpu?.usage > 80 ? 'text-red-400' : 'text-gray-300'}>
                                    {server.metrics.cpu?.usage?.toFixed(1) || 0}%
                                  </span>
                                </div>
                                <div className="w-full h-1.5 bg-gray-700 rounded">
                                  <div
                                    className={`h-full rounded ${server.metrics.cpu?.usage > 80 ? 'bg-red-500' : 'bg-brand-green'}`}
                                    style={{ width: `${Math.min(100, Math.max(0, server.metrics.cpu?.usage || 0))}%` }}
                                  />
                                </div>
                              </div>
                              {/* Memory */}
                              <div>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-400">RAM ({server.metrics.memory?.total_gb?.toFixed(0) || 0} GB)</span>
                                  <span className={server.metrics.memory?.percent > 80 ? 'text-red-400' : 'text-gray-300'}>
                                    {server.metrics.memory?.percent?.toFixed(1) || 0}%
                                  </span>
                                </div>
                                <div className="w-full h-1.5 bg-gray-700 rounded">
                                  <div
                                    className={`h-full rounded ${server.metrics.memory?.percent > 80 ? 'bg-red-500' : 'bg-blue-500'}`}
                                    style={{ width: `${server.metrics.memory?.percent || 0}%` }}
                                  />
                                </div>
                              </div>
                              {/* Disk */}
                              <div>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-400">Disk ({server.metrics.disk?.total_gb?.toFixed(0) || 0} GB)</span>
                                  <span className={server.metrics.disk?.percent > 80 ? 'text-red-400' : 'text-gray-300'}>
                                    {server.metrics.disk?.percent?.toFixed(1) || 0}%
                                  </span>
                                </div>
                                <div className="w-full h-1.5 bg-gray-700 rounded">
                                  <div
                                    className={`h-full rounded ${server.metrics.disk?.percent > 80 ? 'bg-red-500' : 'bg-purple-500'}`}
                                    style={{ width: `${server.metrics.disk?.percent || 0}%` }}
                                  />
                                </div>
                              </div>
                              {/* Extra info */}
                              <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-700/50">
                                <span>{server.server_type}</span>
                                <span>{server.location || 'N/D'}</span>
                              </div>
                            </div>
                          )}

                          {/* Show offline message only if no cached metrics */}
                          {server.status === 'offline' && (!server.metrics || (server.metrics.memory?.total_gb === 0 && server.metrics.disk?.total_gb === 0)) && (
                            <div className="text-center py-3">
                              <p className="text-red-400 text-sm">Serveur inaccessible</p>
                              {server.last_error && (
                                <p className="text-xs text-gray-500 mt-1">{server.last_error}</p>
                              )}
                            </div>
                          )}

                          {server.last_heartbeat && (
                            <div className="text-xs text-gray-500 mt-2 text-right">
                              MAJ: {new Date(server.last_heartbeat).toLocaleString()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Server Modal */}
                  {addServerModal && (
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                      <div className="bg-brand-darker rounded-xl p-6 w-full max-w-md border border-brand-green/30">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold text-white">{t('common.add')} {t('vpn.server')}</h3>
                          <button
                            onClick={() => setAddServerModal(false)}
                            className="text-gray-400 hover:text-white"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Nom *</label>
                            <input
                              type="text"
                              value={newServer.name}
                              onChange={(e) => setNewServer({ ...newServer, name: e.target.value })}
                              className="w-full bg-brand-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-brand-green outline-none"
                              placeholder="Backend Principal"
                            />
                          </div>

                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Adresse IP *</label>
                            <input
                              type="text"
                              value={newServer.ip_address}
                              onChange={(e) => setNewServer({ ...newServer, ip_address: e.target.value })}
                              className="w-full bg-brand-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-brand-green outline-none"
                              placeholder="10.0.1.10"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Port SSH</label>
                              <input
                                type="number"
                                value={newServer.port}
                                onChange={(e) => setNewServer({ ...newServer, port: parseInt(e.target.value) })}
                                className="w-full bg-brand-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-brand-green outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Type</label>
                              <select
                                value={newServer.server_type}
                                onChange={(e) => setNewServer({ ...newServer, server_type: e.target.value })}
                                className="w-full bg-brand-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-brand-green outline-none"
                              >
                                <option value="backend">Backend</option>
                                <option value="worker">Worker</option>
                                <option value="database">Database</option>
                                <option value="cache">Cache</option>
                                <option value="gpu">GPU</option>
                                <option value="scanner">Scanner</option>
                                <option value="vpn">VPN</option>
                                <option value="other">Autre</option>
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Utilisateur SSH</label>
                            <input
                              type="text"
                              value={newServer.ssh_user}
                              onChange={(e) => setNewServer({ ...newServer, ssh_user: e.target.value })}
                              className="w-full bg-brand-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-brand-green outline-none"
                              placeholder="root"
                            />
                          </div>

                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Localisation</label>
                            <input
                              type="text"
                              value={newServer.location}
                              onChange={(e) => setNewServer({ ...newServer, location: e.target.value })}
                              className="w-full bg-brand-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-brand-green outline-none"
                              placeholder="Paris DC1"
                            />
                          </div>

                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Description</label>
                            <textarea
                              value={newServer.description}
                              onChange={(e) => setNewServer({ ...newServer, description: e.target.value })}
                              className="w-full bg-brand-black border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-brand-green outline-none resize-none"
                              rows={2}
                              placeholder="Serveur backend principal..."
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 mt-6">
                          <button
                            onClick={() => setAddServerModal(false)}
                            className="px-4 py-2 text-gray-400 hover:text-white"
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            onClick={addServer}
                            disabled={!newServer.name || !newServer.ip_address}
                            className="px-4 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/80 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {t('common.add')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Server Info Modal */}
                  {serverInfoModal && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                      <div className="bg-[#0a0a0a] rounded-xl p-6 w-full max-w-lg border border-blue-500/50 shadow-2xl">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              serverInfoModal.status === 'online' ? 'bg-green-500/20' :
                              serverInfoModal.status === 'offline' ? 'bg-red-500/20' :
                              'bg-yellow-500/20'
                            }`}>
                              <Server className={`w-6 h-6 ${
                                serverInfoModal.status === 'online' ? 'text-green-400' :
                                serverInfoModal.status === 'offline' ? 'text-red-400' :
                                'text-yellow-400'
                              }`} />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-white">{serverInfoModal.name}</h3>
                              <p className="text-sm text-gray-400">{serverInfoModal.ip_address}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => setServerInfoModal(null)}
                            className="text-gray-400 hover:text-white"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          {/* Server Type Title */}
                          <div className="bg-[#111111] rounded-lg p-4 border border-gray-700">
                            <h4 className="text-lg font-semibold text-blue-400 mb-2">
                              {serverTypeInfo[serverInfoModal.server_type]?.title || serverTypeInfo.other.title}
                            </h4>
                            <p className="text-gray-300 text-sm">
                              {serverTypeInfo[serverInfoModal.server_type]?.description || serverTypeInfo.other.description}
                            </p>
                          </div>

                          {/* Server Details */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-[#1a1a1a] rounded-lg p-3">
                              <span className="text-gray-500 text-xs">Type</span>
                              <p className="text-white font-medium">{serverInfoModal.server_type}</p>
                            </div>
                            <div className="bg-[#1a1a1a] rounded-lg p-3">
                              <span className="text-gray-500 text-xs">Statut</span>
                              <p className={`font-medium ${
                                serverInfoModal.status === 'online' ? 'text-green-400' :
                                serverInfoModal.status === 'offline' ? 'text-red-400' :
                                'text-yellow-400'
                              }`}>{serverInfoModal.status}</p>
                            </div>
                            {serverInfoModal.hostname && (
                              <div className="bg-[#1a1a1a] rounded-lg p-3">
                                <span className="text-gray-500 text-xs">Hostname</span>
                                <p className="text-white font-medium">{serverInfoModal.hostname}</p>
                              </div>
                            )}
                            {serverInfoModal.location && (
                              <div className="bg-[#1a1a1a] rounded-lg p-3">
                                <span className="text-gray-500 text-xs">Localisation</span>
                                <p className="text-white font-medium">{serverInfoModal.location}</p>
                              </div>
                            )}
                          </div>

                          {/* Installed Tools */}
                          <div className="bg-[#111111] rounded-lg p-4 border border-gray-700">
                            <h4 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
                              <Wrench className="w-4 h-4" />
                              Outils installes
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {(serverTypeInfo[serverInfoModal.server_type]?.tools || serverTypeInfo.other.tools).map((tool, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-brand-green/10 text-brand-green text-xs rounded-full border border-brand-green/30"
                                >
                                  {tool}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Metrics if online */}
                          {serverInfoModal.status === 'online' && serverInfoModal.metrics && (
                            <div className="bg-[#111111] rounded-lg p-4 border border-gray-700">
                              <h4 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
                                <Activity className="w-4 h-4" />
                                Current metrics
                              </h4>
                              <div className="grid grid-cols-3 gap-3 text-center">
                                <div>
                                  <div className="text-2xl font-bold text-brand-green">
                                    {serverInfoModal.metrics.cpu?.usage?.toFixed(0) || 0}%
                                  </div>
                                  <div className="text-xs text-gray-500">CPU</div>
                                </div>
                                <div>
                                  <div className="text-2xl font-bold text-blue-400">
                                    {serverInfoModal.metrics.memory?.percent?.toFixed(0) || 0}%
                                  </div>
                                  <div className="text-xs text-gray-500">RAM</div>
                                </div>
                                <div>
                                  <div className="text-2xl font-bold text-purple-400">
                                    {serverInfoModal.metrics.disk?.percent?.toFixed(0) || 0}%
                                  </div>
                                  <div className="text-xs text-gray-500">Disk</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end mt-6">
                          <button
                            onClick={() => setServerInfoModal(null)}
                            className="px-4 py-2 bg-brand-black text-white rounded-lg hover:bg-gray-700"
                          >
                            {t('common.close')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* License Tab */}
              {activeTab === 'license' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Gestion de la Licence</h2>
                    <p className="text-gray-400">Configuration licence et quotas</p>
                  </div>
                  <LicenseSettings />
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Security</h2>
                    <p className="text-gray-400">Security configuration and authentication</p>
                  </div>
                  <SecuritySettings />
                </div>
              )}

              {/* VPN Tab */}
              {activeTab === 'vpn' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-brand-green mb-2">{t('settings.networkSection.title')}</h2>
                      <p className="text-gray-400">Configuration des tunnels VPN externes et internes</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={fetchVpnConfigs}
                        className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex items-center gap-2"
                      >
                        <RefreshCw className={`w-4 h-4 ${vpnLoading ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => setVpnAddModal(true)}
                        className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/80 flex items-center gap-2 font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        {t('common.add')} VPN
                      </button>
                    </div>
                  </div>

                  {/* VPN Status Banner */}
                  {vpnStatus.status === 'connected' ? (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                          <ShieldCheck className="w-6 h-6 text-green-400" />
                        </div>
                        <div>
                          <div className="text-green-400 font-bold flex items-center gap-2">
                            VPN Connecte
                            {vpnStatus.country_code && (
                              <span className="text-lg">{vpnStatus.country_code === 'FR' ? '\ud83c\uddeb\ud83c\uddf7' : vpnStatus.country_code === 'US' ? '\ud83c\uddfa\ud83c\uddf8' : vpnStatus.country_code === 'DE' ? '\ud83c\udde9\ud83c\uddea' : vpnStatus.country_code === 'GB' ? '\ud83c\uddec\ud83c\udde7' : vpnStatus.country_code === 'NL' ? '\ud83c\uddf3\ud83c\uddf1' : vpnStatus.country_code === 'CH' ? '\ud83c\udde8\ud83c\udded' : '\ud83c\udf10'}</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-400">
                            {vpnStatus.config_name || 'Configuration active'}
                          </div>
                          <div className="text-sm text-cyan-400 font-mono mt-1">
                            IP: {vpnStatus.public_ip || 'N/D'} {vpnStatus.city && vpnStatus.country && `- ${vpnStatus.city}, ${vpnStatus.country}`}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleVpnDisconnect}
                        disabled={connectingVpn === 'disconnecting'}
                        className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center gap-2"
                      >
                        {connectingVpn === 'disconnecting' ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Power className="w-4 h-4" />
                        )}
                        Disconnect
                      </button>
                    </div>
                  ) : (
                    <div className="bg-gray-500/10 border border-gray-500/30 rounded-xl p-4">
                      {/* Header avec statut */}
                      <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-700">
                        <div className="w-12 h-12 rounded-full bg-gray-500/20 flex items-center justify-center">
                          <Globe className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="flex-1">
                          <div className="text-gray-400 font-bold flex items-center gap-2">
                            VPN Disconnected
                            {vpnStatus.country_code && (
                              <span className="text-lg">{vpnStatus.country_code === 'FR' ? '\ud83c\uddeb\ud83c\uddf7' : vpnStatus.country_code === 'US' ? '\ud83c\uddfa\ud83c\uddf8' : vpnStatus.country_code === 'DE' ? '\ud83c\udde9\ud83c\uddea' : vpnStatus.country_code === 'GB' ? '\ud83c\uddec\ud83c\udde7' : vpnStatus.country_code === 'NL' ? '\ud83c\uddf3\ud83c\uddf1' : vpnStatus.country_code === 'CH' ? '\ud83c\udde8\ud83c\udded' : '\ud83c\udf10'}</span>
                            )}
                          </div>
                          <div className="text-sm text-orange-400 font-mono">
                            IP: {vpnStatus.public_ip || 'N/D'} {vpnStatus.city && vpnStatus.country && `- ${vpnStatus.city}, ${vpnStatus.country}`}
                          </div>
                        </div>
                      </div>

                      {/* Options de connexion rapide */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* VPN Externe (Premier disponible) */}
                        <div className={`p-4 rounded-xl border transition-all duration-300 ${
                          connectingVpn && vpnConfigs.externe?.[0] && connectingVpn === vpnConfigs.externe[0].id
                            ? 'border-cyan-500 bg-cyan-500/10 animate-pulse'
                            : 'border-cyan-500/30 bg-cyan-500/5 hover:border-cyan-500/50'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-lg">
                                {'\ud83e\udd88'}
                              </div>
                              <div>
                                <div className="font-medium text-white text-sm">VPN Externe</div>
                                {vpnConfigs.externe?.[0] ? (
                                  <div className="text-xs text-cyan-400">{vpnConfigs.externe[0].name}</div>
                                ) : (
                                  <div className="text-xs text-gray-500">None configured</div>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => vpnConfigs.externe?.[0] && handleVpnConnect(vpnConfigs.externe[0].id, true)}
                              disabled={!vpnConfigs.externe?.[0] || connectingVpn}
                              className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                                !vpnConfigs.externe?.[0] || connectingVpn
                                  ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                                  : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30'
                              }`}
                            >
                              {connectingVpn && vpnConfigs.externe?.[0] && connectingVpn === vpnConfigs.externe[0].id ? (
                                <>
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                  <span>{t('vpn.connecting')}</span>
                                </>
                              ) : (
                                <>
                                  <Power className="w-4 h-4" />
                                  <span>{t('vpn.connect')}</span>
                                </>
                              )}
                            </button>
                          </div>
                          {/* Barre de progression */}
                          {connectingVpn && vpnConfigs.externe?.[0] && connectingVpn === vpnConfigs.externe[0].id && (
                            <div className="mt-3">
                              <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-300 rounded-full animate-pulse" style={{width: '100%'}}></div>
                              </div>
                              <div className="text-xs text-cyan-400 mt-1 text-center">Etablissement du tunnel VPN...</div>
                            </div>
                          )}
                        </div>

                        {/* VPN Interne (Premier disponible) */}
                        <div className={`p-4 rounded-xl border transition-all duration-300 ${
                          connectingVpn && vpnConfigs.interne?.[0] && connectingVpn === vpnConfigs.interne[0].id
                            ? 'border-purple-500 bg-purple-500/10 animate-pulse'
                            : 'border-purple-500/30 bg-purple-500/5 hover:border-purple-500/50'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                <Lock className="w-5 h-5 text-purple-400" />
                              </div>
                              <div>
                                <div className="font-medium text-white text-sm">VPN Interne</div>
                                {vpnConfigs.interne?.[0] ? (
                                  <div className="text-xs text-purple-400">{vpnConfigs.interne[0].name}</div>
                                ) : (
                                  <div className="text-xs text-gray-500">None configured</div>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => vpnConfigs.interne?.[0] && handleVpnConnect(vpnConfigs.interne[0].id, vpnConfigs.interne[0].requires_auth)}
                              disabled={!vpnConfigs.interne?.[0] || connectingVpn}
                              className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                                !vpnConfigs.interne?.[0] || connectingVpn
                                  ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                                  : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                              }`}
                            >
                              {connectingVpn && vpnConfigs.interne?.[0] && connectingVpn === vpnConfigs.interne[0].id ? (
                                <>
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                  <span>{t('vpn.connecting')}</span>
                                </>
                              ) : (
                                <>
                                  <Power className="w-4 h-4" />
                                  <span>{t('vpn.connect')}</span>
                                </>
                              )}
                            </button>
                          </div>
                          {/* Barre de progression */}
                          {connectingVpn && vpnConfigs.interne?.[0] && connectingVpn === vpnConfigs.interne[0].id && (
                            <div className="mt-3">
                              <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-purple-500 to-purple-300 rounded-full animate-pulse" style={{width: '100%'}}></div>
                              </div>
                              <div className="text-xs text-purple-400 mt-1 text-center">Etablissement du tunnel VPN...</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Message d'erreur VPN */}
                      {vpnConnectionError && (
                        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                          <span className="text-red-400 text-sm">{vpnConnectionError}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* VPN Stats Overview */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-brand-black rounded-xl p-4 border border-cyan-500/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                          <Globe className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-cyan-400">
                            {vpnConfigs.externe?.length || 0}
                          </div>
                          <div className="text-xs text-gray-500">VPN Externes</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-brand-black rounded-xl p-4 border border-purple-500/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <Lock className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-purple-400">
                            {vpnConfigs.interne?.length || 0}
                          </div>
                          <div className="text-xs text-gray-500">VPN Internes</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-brand-black rounded-xl p-4 border border-green-500/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <ShieldCheck className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-400">
                            {vpnStatus.status === 'connected' ? '1' : '0'}
                          </div>
                          <div className="text-xs text-gray-500">Connected</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-brand-black rounded-xl p-4 border border-blue-500/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <Database className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-blue-400">
                            {(vpnConfigs.externe?.length || 0) + (vpnConfigs.interne?.length || 0)}
                          </div>
                          <div className="text-xs text-gray-500">Configurations</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* VPN External (SYLink VPN) */}
                  <div className="bg-brand-black rounded-xl border border-cyan-500/30 overflow-hidden">
                    <div className="p-4 border-b border-cyan-500/20 bg-cyan-500/5 flex items-center justify-between">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Globe className="w-5 h-5 text-cyan-400" />
                        VPN Externes (Anonymisation)
                        <span className="ml-2 text-xs px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">SYLink VPN</span>
                      </h3>
                      <input
                        type="text"
                        placeholder="Filtrer par pays..."
                        value={vpnFilter}
                        onChange={(e) => setVpnFilter(e.target.value)}
                        className="px-3 py-1 bg-brand-black-light border border-gray-700 rounded text-sm text-white w-48"
                      />
                    </div>
                    <div className="p-4 max-h-96 overflow-y-auto">
                      {vpnLoading ? (
                        <div className="text-center py-8 text-gray-500">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                          {t('common.loading')}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {(vpnConfigs.externe || [])
                            .filter(cfg => !vpnFilter || cfg.name.toLowerCase().includes(vpnFilter.toLowerCase()) || cfg.country?.toLowerCase().includes(vpnFilter.toLowerCase()))
                            .slice(0, 50)
                            .map(cfg => (
                            <div key={cfg.id} className={`p-3 rounded-lg border ${
                              vpnStatus.config_id === cfg.id ? 'border-green-500/50 bg-green-500/10' : 'border-gray-700 bg-brand-black-light hover:border-cyan-500/30'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-brand-green/20 flex items-center justify-center text-lg">
                                    🔒
                                  </div>
                                  <div>
                                    <div className="font-medium text-white text-sm">{cfg.name}</div>
                                    <div className="text-xs text-gray-500">{cfg.remote_host}:{cfg.remote_port}</div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => vpnStatus.config_id === cfg.id ? handleVpnDisconnect() : handleVpnConnect(cfg.id, true)}
                                  disabled={connectingVpn === cfg.id || connectingVpn === 'disconnecting'}
                                  className={`p-2 rounded-lg ${
                                    vpnStatus.config_id === cfg.id
                                      ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                      : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                  }`}
                                >
                                  {connectingVpn === cfg.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Power className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {!vpnLoading && (vpnConfigs.externe || []).length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          Aucun VPN externe configure
                        </div>
                      )}
                      {!vpnLoading && (vpnConfigs.externe || []).length > 50 && (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          Affichage limite a 50 configurations. Utilisez le filtre pour trouver un pays specifique.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* VPN Internal (Enterprise) */}
                  <div className="bg-brand-black rounded-xl border border-purple-500/30 overflow-hidden">
                    <div className="p-4 border-b border-purple-500/20 bg-purple-500/5">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Lock className="w-5 h-5 text-purple-400" />
                        VPN Internes (Entreprise)
                        <span className="ml-2 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">OpenVPN</span>
                      </h3>
                    </div>
                    <div className="p-4 space-y-3">
                      {vpnLoading ? (
                        <div className="text-center py-8 text-gray-500">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                          {t('common.loading')}
                        </div>
                      ) : (
                        (vpnConfigs.interne || []).map(cfg => (
                          <div key={cfg.id} className={`p-4 rounded-lg border ${
                            vpnStatus.config_id === cfg.id ? 'border-green-500/50 bg-green-500/10' : 'border-gray-700 bg-brand-black-light'
                          }`}>
                            <div className="flex items-center justify-between flex-wrap gap-4">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                                  <FileKey className="w-6 h-6 text-purple-400" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-bold text-white">{cfg.name}</span>
                                    {vpnStatus.config_id === cfg.id && (
                                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">Connecte</span>
                                    )}
                                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                                      {cfg.protocol?.toUpperCase() || 'OpenVPN'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                                    <span className="flex items-center gap-1">
                                      <Server className="w-3 h-3" />
                                      {cfg.remote_host}:{cfg.remote_port}
                                    </span>
                                    {cfg.requires_auth && (
                                      <span className="flex items-center gap-1 text-yellow-400">
                                        <Key className="w-3 h-3" />
                                        Auth requise
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => vpnStatus.config_id === cfg.id ? handleVpnDisconnect() : handleVpnConnect(cfg.id, cfg.requires_auth)}
                                  disabled={connectingVpn === cfg.id || connectingVpn === 'disconnecting'}
                                  className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium ${
                                    vpnStatus.config_id === cfg.id
                                      ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                      : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                  }`}
                                >
                                  {connectingVpn === cfg.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Power className="w-4 h-4" />
                                  )}
                                  {vpnStatus.config_id === cfg.id ? t('vpn.disconnect') : t('vpn.connect')}
                                </button>
                                <button
                                  onClick={() => handleVpnDelete(cfg.id)}
                                  className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                                >
                                  <Trash className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                      {!vpnLoading && (vpnConfigs.interne || []).length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          No internal VPN configured. Click "Add VPN" to import an .ovpn configuration.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* VPN Upload Modal */}
                  {vpnAddModal && (
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                      <div className="bg-brand-black border border-gray-700 rounded-xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold text-white">{t('common.add')} VPN</h3>
                          <button onClick={() => { setVpnAddModal(false); setVpnUploadFile(null); }} className="text-gray-400 hover:text-white">
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-2">Type de VPN</label>
                            <select
                              value={vpnUploadType}
                              onChange={(e) => setVpnUploadType(e.target.value)}
                              className="w-full px-4 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white"
                            >
                              <option value="externe">Externe (SYLink VPN)</option>
                              <option value="interne">Interne (Entreprise)</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm text-gray-400 mb-2">Fichier de configuration (.ovpn)</label>
                            <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center">
                              <input
                                type="file"
                                accept=".ovpn,.conf"
                                onChange={(e) => setVpnUploadFile(e.target.files[0])}
                                className="hidden"
                                id="vpn-file-upload"
                              />
                              <label htmlFor="vpn-file-upload" className="cursor-pointer">
                                <FolderUp className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                                {vpnUploadFile ? (
                                  <div className="text-brand-green">{vpnUploadFile.name}</div>
                                ) : (
                                  <div className="text-gray-500">Click to select an .ovpn file</div>
                                )}
                              </label>
                            </div>
                          </div>

                          {vpnUploadType === 'interne' && (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Nom d'utilisateur (optionnel)</label>
                                <input
                                  type="text"
                                  value={vpnCredentials.username}
                                  onChange={(e) => setVpnCredentials(prev => ({ ...prev, username: e.target.value }))}
                                  className="w-full px-4 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white"
                                  placeholder="Entrez votre identifiant"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Mot de passe (optionnel)</label>
                                <input
                                  type="password"
                                  value={vpnCredentials.password}
                                  onChange={(e) => setVpnCredentials(prev => ({ ...prev, password: e.target.value }))}
                                  className="w-full px-4 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white"
                                  placeholder="Entrez votre mot de passe"
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-3 mt-6">
                          <button
                            onClick={() => { setVpnAddModal(false); setVpnUploadFile(null); }}
                            className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            onClick={handleVpnUpload}
                            disabled={!vpnUploadFile}
                            className="flex-1 px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <Upload className="w-4 h-4" />
                            Importer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* VPN Credentials Modal */}
                  {vpnCredentialsModal && (
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                      <div className="bg-brand-black border border-gray-700 rounded-xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Key className="w-5 h-5 text-brand-green" />
                            {t('vpn.connect')} VPN
                          </h3>
                          <button
                            onClick={() => setVpnCredentialsModal(null)}
                            className="text-gray-400 hover:text-white"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          {/* VPN Info */}
                          <div className="bg-brand-black-light rounded-lg p-4 border border-gray-700">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-xl">
                                {vpnCredentialsModal.type === 'externe' ? '🦈' : '🏢'}
                              </div>
                              <div>
                                <div className="font-medium text-white">{vpnCredentialsModal.name}</div>
                                <div className="text-xs text-gray-500">
                                  {vpnCredentialsModal.remote_host}:{vpnCredentialsModal.remote_port}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Username */}
                          <div>
                            <label className="block text-sm text-gray-400 mb-2">
                              Nom d'utilisateur {vpnCredentialsModal.type === 'externe' && <span className="text-brand-green">(SYLink VPN)</span>}
                            </label>
                            <input
                              type="text"
                              value={vpnCredentials.username}
                              onChange={(e) => setVpnCredentials(prev => ({ ...prev, username: e.target.value }))}
                              className="w-full px-4 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                              placeholder="Entrez votre identifiant"
                              autoFocus
                            />
                          </div>

                          {/* Password */}
                          <div>
                            <label className="block text-sm text-gray-400 mb-2">Mot de passe</label>
                            <input
                              type="password"
                              value={vpnCredentials.password}
                              onChange={(e) => setVpnCredentials(prev => ({ ...prev, password: e.target.value }))}
                              className="w-full px-4 py-2 bg-brand-black-light border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                              placeholder="Entrez votre mot de passe"
                              onKeyPress={(e) => e.key === 'Enter' && handleVpnConnectWithCredentials()}
                            />
                          </div>

                          {/* Save credentials checkbox */}
                          <div className="flex items-center gap-3 p-3 bg-brand-black-light rounded-lg border border-gray-700">
                            <input
                              type="checkbox"
                              id="saveCredentials"
                              checked={vpnSaveCredentials}
                              onChange={(e) => setVpnSaveCredentials(e.target.checked)}
                              className="w-4 h-4 rounded border-gray-600 text-brand-green focus:ring-brand-green focus:ring-offset-gray-800"
                            />
                            <label htmlFor="saveCredentials" className="text-sm text-gray-300 cursor-pointer">
                              {t('vpn.saveCredentials')}
                            </label>
                          </div>

                          {/* Info about saved credentials */}
                          {savedVpnCredentials[vpnCredentialsModal.id] && (
                            <div className="text-xs text-green-400 flex items-center gap-2 px-2">
                              <CheckCircle className="w-3 h-3" />
                              Saved credentials for this VPN
                            </div>
                          )}

                          {/* Error message */}
                          {vpnConnectionError && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-3">
                              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                              <div>
                                <div className="text-red-400 font-medium text-sm">Connection failed</div>
                                <div className="text-red-300 text-xs mt-1">{vpnConnectionError}</div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-3 mt-6">
                          <button
                            onClick={() => { setVpnCredentialsModal(null); setVpnConnectionError(null); }}
                            disabled={vpnConnectingInModal}
                            className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50"
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            onClick={handleVpnConnectWithCredentials}
                            disabled={!vpnCredentials.username || !vpnCredentials.password || vpnConnectingInModal}
                            className="flex-1 px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                          >
                            {vpnConnectingInModal ? (
                              <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                {t('vpn.connecting')}
                              </>
                            ) : (
                              <>
                                <Power className="w-4 h-4" />
                                {t('vpn.connect')}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* VPN Configuration Tips */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                    <h4 className="text-blue-400 font-bold mb-3 flex items-center gap-2">
                      <Brain className="w-5 h-5" />
                      Conseils d'utilisation VPN
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-300">
                      <div className="space-y-2">
                        <h5 className="text-white font-medium">VPN Externes (SYLink VPN)</h5>
                        <ul className="list-disc list-inside text-gray-400 space-y-1">
                          <li>Utilisez pour anonymiser vos tests de pentest</li>
                          <li>Choisissez un serveur proche pour la latence</li>
                          <li>Les credentials SYLink VPN sont requis</li>
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h5 className="text-white font-medium">VPN Internes (Entreprise)</h5>
                        <ul className="list-disc list-inside text-gray-400 space-y-1">
                          <li>Access to client internal networks</li>
                          <li>Importez les fichiers .ovpn fournis</li>
                          <li>Verify authentication credentials</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Proxy Tab */}
              {activeTab === 'proxy' && (
                <ProxySettings />
              )}

              {/* Orchestrator Tab */}
              {activeTab === 'orchestrator' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Orchestrateur IA</h2>
                    <p className="text-gray-400">Monitoring orchestrateur d'attaques IA</p>
                  </div>
                  <OrchestratorMonitoringEmbed embedded={true} />

                  {/* AI Decisions Section */}
                  <AIDecisionsConfig />

                  {/* Section API Keys LLM externes */}
                  <AIApiKeysConfig />
                </div>
              )}

              {/* AI Tab */}
              {activeTab === 'ai' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">AI Reasoning</h2>
                    <p className="text-gray-400">Workflow de decision autonome</p>
                  </div>
                  <AIWorkflowVisual />
                </div>
              )}

              {/* GPU Tab */}
              {activeTab === 'gpu' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">GPU & LLM Acceleration</h2>
                    <p className="text-gray-400">GPU configuration for AI acceleration</p>
                  </div>
                  <GPUInfo />
                </div>
              )}

              {/* Attack Matrix Tab */}
              {activeTab === 'attack-matrix' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Matrice des Attaques</h2>
                    <p className="text-gray-400">Configuration LLM pour matrices d'attaques</p>
                  </div>
                  <AttackMatrixConfig />
                </div>
              )}

              {/* LLM Logic Tab */}
              {activeTab === 'llm-logic' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Logique LLM Pentests</h2>
                    <p className="text-gray-400">Configuration du raisonnement IA</p>
                  </div>
                  <LLMLogicConfig />
                </div>
              )}

              {/* AI Agent Tab - Superadmin only */}
              {activeTab === 'ai-agent' && isSuperAdmin && (
                <AIAgentDashboard />
              )}

              {/* AI Assistant Tab - Superadmin only */}
              {activeTab === 'ai-assistant' && isSuperAdmin && (
                <AIAssistantSettings />
              )}

              {/* Tools Tab */}
              {activeTab === 'tools' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">{t('settings.toolsSection.title')}</h2>
                    <p className="text-gray-400">Configuration des outils disponibles</p>
                  </div>
                  <ToolsList />
                </div>
              )}

              {/* Orchestrator Tools Tab */}
              {activeTab === 'orchestrator-tools' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Outils Orchestrateur</h2>
                    <p className="text-gray-400">Dynamic Orchestrator tools configuration with system status</p>
                  </div>
                  <DynamicOrchestratorTools />
                </div>
              )}

              {/* Reports Tab */}
              {activeTab === 'reports' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Rapports</h2>
                    <p className="text-gray-400">Configuration des rapports et gestion des clients</p>
                  </div>
                  <ReportsConfig />
                </div>
              )}

              {/* Connectors Tab */}
              {activeTab === 'connectors' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Connecteurs d'enrichissement OSINT</h2>
                    <p className="text-gray-400">Configure data sources for leak searching and credential enrichment</p>
                  </div>
                  <ConnectorsConfig />
                </div>
              )}

              {/* Contract Tab */}
              {activeTab === 'contract' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Contrat Client</h2>
                    <p className="text-gray-400">Gestion des contrats d'autorisation de test d'intrusion et signature electronique</p>
                  </div>
                  <ContractManager />
                </div>
              )}

              {/* Modules Tab */}
              {activeTab === 'modules' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Modules</h2>
                    <p className="text-gray-400">Configuration des modules SYLINK</p>
                  </div>
                  <ModulesConfiguration />
                </div>
              )}

              {/* Logs Tab */}
              {activeTab === 'logs' && (
                <div className="h-full flex flex-col">
                  <div className="flex-shrink-0 mb-4">
                    <h2 className="text-2xl font-bold text-brand-green mb-2">System Logs</h2>
                    <p className="text-gray-400">Visualisation temps reel</p>
                  </div>
                  <LogsViewer fullHeight />
                </div>
              )}

              {/* Tracking Logs Tab - Superadmin Only */}
              {activeTab === 'tracking' && isSuperAdmin && (
                <div className="h-full flex flex-col">
                  <div className="flex-shrink-0 mb-4">
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Journaux de Suivi</h2>
                    <p className="text-gray-400">Suivi des actions utilisateurs - Audit et diagnostic</p>
                  </div>
                  <TrackingLogs />
                </div>
              )}

              {/* Audit API Logs Tab - Superadmin Only */}
              {activeTab === 'audit' && isSuperAdmin && (
                <div className="h-full flex flex-col">
                  <div className="flex-shrink-0 mb-4">
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Journaux API</h2>
                    <p className="text-gray-400">System logs of API requests - Debug and customer support</p>
                  </div>
                  <AuditLogs />
                </div>
              )}

              {/* Clients Management Tab - Superadmin Only */}
              {activeTab === 'clients' && isSuperAdmin && (
                <div className="h-full flex flex-col">
                  <div className="flex-shrink-0 mb-4">
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Gestion des Clients</h2>
                    <p className="text-gray-400">Administration des comptes utilisateurs inscrits sur la plateforme</p>
                  </div>

                  {/* Demo Invitation Section */}
                  <div className="flex-shrink-0 mb-6 bg-gray-800 border border-gray-700 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-purple-500/20 rounded-lg">
                        <Rocket className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Invite a prospect to a demo</h3>
                        <p className="text-sm text-gray-400">Send an invitation link to create a free demo account (valid 7 days, no approval required)</p>
                      </div>
                    </div>

                    {demoInviteSuccess && (
                      <div className="flex items-center gap-2 mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-green-400 text-sm">{demoInviteSuccess}</span>
                      </div>
                    )}

                    <form onSubmit={sendDemoInvitation} className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Email du prospect <span className="text-red-400">*</span></label>
                          <input
                            type="email"
                            required
                            value={demoInviteForm.email}
                            onChange={e => setDemoInviteForm(f => ({ ...f, email: e.target.value }))}
                            placeholder="prospect@entreprise.com"
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Entreprise</label>
                          <input
                            type="text"
                            value={demoInviteForm.companyName}
                            onChange={e => setDemoInviteForm(f => ({ ...f, companyName: e.target.value }))}
                            placeholder="Nom de l'entreprise"
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">First name</label>
                          <input
                            type="text"
                            value={demoInviteForm.firstName}
                            onChange={e => setDemoInviteForm(f => ({ ...f, firstName: e.target.value }))}
                            placeholder="First name"
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Nom</label>
                          <input
                            type="text"
                            value={demoInviteForm.lastName}
                            onChange={e => setDemoInviteForm(f => ({ ...f, lastName: e.target.value }))}
                            placeholder="Nom de famille"
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Custom message (optional)</label>
                        <textarea
                          value={demoInviteForm.message}
                          onChange={e => setDemoInviteForm(f => ({ ...f, message: e.target.value }))}
                          placeholder="Add a custom message to include in the invitation email..."
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                        />
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          L'invitation permet de s'inscrire avec n'importe quel email (pro ou personnel)
                        </p>
                        <button
                          type="submit"
                          disabled={demoInviteLoading || !demoInviteForm.email}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          {demoInviteLoading ? (
                            <><RefreshCw className="w-4 h-4 animate-spin" /> Envoi en cours...</>
                          ) : (
                            <><Rocket className="w-4 h-4" /> Envoyer l'invitation</>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                  <ClientsManagement />
                </div>
              )}

              {/* Updates Tab */}
              {activeTab === 'updates' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-brand-green mb-2">Updates</h2>
                    <p className="text-gray-400">Gestion des mises a jour</p>
                  </div>
                  <UpdatesManager />
                </div>
              )}

              {/* Credits Tab */}
              {activeTab === 'credits' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Subscription & Credits</h2>
                    <p className="text-gray-400">Manage your subscription and credits</p>
                  </div>
                  <CreditsSettings />
                </div>
              )}

              {/* Admin Credits Tab - Superadmin only */}
              {activeTab === 'admin-credits' && isSuperAdmin && (
                <div className="space-y-6">
                  <CreditsAdminPanel />
                </div>
              )}

              {/* Reset Tab */}
              {activeTab === 'reset' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-red-400 mb-2">Reset - Danger Zone</h2>
                    <p className="text-gray-400">Irreversible data deletion</p>
                  </div>
                  <ResetDataSettings />
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Wizards */}
      {activeWizard === 'splunk' && (
        <SplunkWizard onClose={() => setActiveWizard(null)} onSave={handleSaveIntegration} />
      )}

      {activeWizard === 'jira' && (
        <JiraWizard onClose={() => setActiveWizard(null)} onSave={handleSaveIntegration} />
      )}

      {['teams', 'discord', 'mattermost'].includes(activeWizard) && (
        <GenericWebhookWizard platform={activeWizard} onClose={() => setActiveWizard(null)} onSave={handleSaveIntegration} />
      )}

      {activeWizard === 'email' && (
        <EmailWizard onClose={() => setActiveWizard(null)} onSave={handleSaveIntegration} />
      )}

      <IntegrationManualConfigModal
        integration={manualConfigModal}
        isOpen={manualConfigModal !== null}
        onClose={() => setManualConfigModal(null)}
      />
    </div>
  );
};

export default Settings;
